import React, { useState, useEffect, useCallback, useMemo } from "react";
import drugData from "./pharmdrill_master_list.json";
import pathwayData from "./pharmdrill_pathways.json";
import inhalerData from "./pharmdrill_inhalers.json";
import { Analytics } from "@vercel/analytics/react";

// ══════════════════════════════════════════════════════════════════════
// ── Drug Data — imported from pharmdrill_master_list.json
// ══════════════════════════════════════════════════════════════════════
//
// The JSON stores drugs with an `indications` array. Each indication has:
//   category, classLabel, primaryAction, cascade, notes
//
// We flatten them here so every mode (Flashcards, Match, Quiz) receives
// the same flat shape they always expected: drug.category, drug.cascade, etc.
//
// Drugs with multiple indications (e.g. Lidocaine: Antiarrhythmic + Local
// Anesthetic) become two separate flat entries with ids like
// "lidocaine" and "lidocaine_ind1".
//
// To add or edit a drug: edit pharmdrill_master_list.json, not this file.
//

interface DrugEntry {
  id: string;
  generic: string;
  trade: string | null;
  category: string;
  classLabel: string;
  primaryAction: string;
  cascade: string;
  notes: string;
  _indicationIndex: number;
}

const DRUGS: DrugEntry[] = (drugData as any[]).flatMap((d: any) =>
  d.indications.map((ind: any, idx: number) => ({
    id: idx === 0 ? d.id : `${d.id}_ind${idx}`,
    generic: d.generic,
    trade: d.trade ?? null,
    category: ind.category,
    classLabel: ind.classLabel,
    primaryAction: ind.primaryAction ?? "",
    cascade: ind.cascade ?? "",
    notes: ind.notes ?? "",
    _indicationIndex: idx,
  }))
);

const CATEGORIES = [...new Set(DRUGS.map((d) => d.category))];

// Fixed drug set for the Inhaler Name Match mode (ignores the category filter).
// ICS are matched by classLabel because the "Corticosteroid" category also
// holds systemic steroids (prednisone, dexamethasone, hydrocortisone).
const INHALER_CATEGORIES = new Set(["SABA", "SAMA", "LABA", "LAMA", "Combo Inhaler"]);
const INHALER_DRUGS = DRUGS.filter(
  (d) => INHALER_CATEGORIES.has(d.category) || d.classLabel === "ICS"
);

const CAT_COLORS: Record<string, { bg: string; border: string; text: string }> =
  {
    // ── Respiratory ──
    SABA: { bg: "#e0f2fe", border: "#0284c7", text: "#0c4a6e" },
    LABA: { bg: "#dbeafe", border: "#2563eb", text: "#1e3a5f" },
    SAMA: { bg: "#fce7f3", border: "#db2777", text: "#831843" },
    LAMA: { bg: "#fdf2f8", border: "#ec4899", text: "#9d174d" },
    "Combo Inhaler": { bg: "#faf5ff", border: "#a855f7", text: "#581c87" },
    LTRA: { bg: "#f0fdf4", border: "#16a34a", text: "#14532d" },
    Mucolytic: { bg: "#f0fdfa", border: "#0d9488", text: "#134e4a" },
    Expectorant: { bg: "#ecfdf5", border: "#10b981", text: "#065f46" },
    "Mucus Drying Agent": { bg: "#fef9c3", border: "#ca8a04", text: "#713f12" },
    Antitussive: { bg: "#fff7ed", border: "#f97316", text: "#7c2d12" },
    "Other Respiratory Drugs": {
      bg: "#f0f9ff",
      border: "#0ea5e9",
      text: "#0c4a6e",
    },
    Xanthines: { bg: "#fef3c7", border: "#d97706", text: "#78350f" },
    "Xanthines / Methylxanthines": {
      bg: "#fef3c7",
      border: "#d97706",
      text: "#78350f",
    },
    PDE_4: { bg: "#fff7ed", border: "#ea580c", text: "#7c2d12" },
    "PDE Inhibitor": { bg: "#fff7ed", border: "#ea580c", text: "#7c2d12" },

    // ── Anti-inflammatory / Biologic ──
    "Anti-inflammatory": { bg: "#ede9fe", border: "#7c3aed", text: "#4c1d95" },
    Corticosteroid: { bg: "#ede9fe", border: "#7c3aed", text: "#4c1d95" },
    Biologics: { bg: "#e0e7ff", border: "#4f46e5", text: "#312e81" },
    Biologic: { bg: "#e0e7ff", border: "#4f46e5", text: "#312e81" },

    // ── Neuromuscular ──
    NMB: { bg: "#fef3c7", border: "#d97706", text: "#78350f" },
    "Neuromuscular Blocker": {
      bg: "#fef3c7",
      border: "#d97706",
      text: "#78350f",
    },
    "Reversal Agent": { bg: "#d1fae5", border: "#059669", text: "#064e3b" },
    "Post-Sypnatic Muscle Relaxant*": {
      bg: "#fef2f2",
      border: "#ef4444",
      text: "#991b1b",
    },
    "Cholinergic Agent": { bg: "#f0fdf4", border: "#22c55e", text: "#14532d" },

    // ── Cardiac / Vascular ──
    Antiarrhythmic: { bg: "#1e3a5f", border: "#60a5fa", text: "#bfdbfe" },
    "Antiarrhythmic, Anesthetics": {
      bg: "#1e3a5f",
      border: "#60a5fa",
      text: "#bfdbfe",
    },
    Chronotrope: { bg: "#3d1f00", border: "#fbbf24", text: "#fde68a" },
    Inodilator: { bg: "#2e1065", border: "#c084fc", text: "#e9d5ff" },
    "Inodilator, PDE Inhibitor": {
      bg: "#2e1065",
      border: "#c084fc",
      text: "#e9d5ff",
    },
    Inotrope: { bg: "#1e1b4b", border: "#818cf8", text: "#c7d2fe" },
    Vasodilator: { bg: "#042f2e", border: "#2dd4bf", text: "#99f6e4" },
    Vasopressor: { bg: "#450a0a", border: "#f87171", text: "#fee2e2" },
    "Pulmonary Vasodilator": {
      bg: "#082f49",
      border: "#7dd3fc",
      text: "#e0f2fe",
    },

    // ── Fluids / Blood ──
    "Blood Products": { bg: "#3b0a0a", border: "#f87171", text: "#fecaca" },
    "Blood Product": { bg: "#3b0a0a", border: "#f87171", text: "#fecaca" },
    Colloid: { bg: "#2d1f0e", border: "#d97706", text: "#fcd34d" },
    Crystalloid: { bg: "#0f2744", border: "#38bdf8", text: "#bae6fd" },
    "Volume Replacement": { bg: "#0f2744", border: "#38bdf8", text: "#bae6fd" },
    Diuretic: { bg: "#052e16", border: "#4ade80", text: "#bbf7d0" },

    // ── Coagulation ──
    Anticoagulant: { bg: "#1c1917", border: "#f59e0b", text: "#fde68a" },
    Antiplatelet: { bg: "#1c1917", border: "#fbbf24", text: "#fef3c7" },
    Thrombolytic: { bg: "#3b0f0f", border: "#f87171", text: "#fecaca" },
    "Anti-fibrinolytic": { bg: "#1a0a00", border: "#fb923c", text: "#fed7aa" },

    // ── Anesthesia / Sedation / Analgesia ──
    "IV Anesthetic": { bg: "#1e1b4b", border: "#818cf8", text: "#c7d2fe" },
    Sedative: { bg: "#1e1b4b", border: "#a78bfa", text: "#ddd6fe" },
    "Opioid Analgesic": { bg: "#2d1657", border: "#c084fc", text: "#e9d5ff" },
    "Local Anesthetic": { bg: "#0c1a2e", border: "#7dd3fc", text: "#bae6fd" },

    // ── Autonomic ──
    Anticholinergic: { bg: "#fff1f2", border: "#f43f5e", text: "#881337" },
    Antidote: { bg: "#fefce8", border: "#ca8a04", text: "#713f12" },
    "Gastrointestinal Decontaminant*": {
      bg: "#fefce8",
      border: "#ca8a04",
      text: "#713f12",
    },

    // ── Anti-infectives ──
    Antibiotic: { bg: "#042f2e", border: "#6ee7b7", text: "#a7f3d0" },
    Antiviral: { bg: "#0c2b0c", border: "#86efac", text: "#bbf7d0" },
    Antifungal: { bg: "#1a0a00", border: "#fb923c", text: "#fed7aa" },
    Antiprotozoal: { bg: "#1c1917", border: "#a3e635", text: "#d9f99d" },
    Antineoplastic: { bg: "#1a001a", border: "#d946ef", text: "#f5d0fe" },
    "Anti-TB": { bg: "#1a0a00", border: "#f59e0b", text: "#fef3c7" },
    "Anti-Histamine": { bg: "#fdf2f8", border: "#ec4899", text: "#9d174d" },
    Antipsychotic: { bg: "#1e1b4b", border: "#818cf8", text: "#c7d2fe" },

    // ── GI ──
    PPI: { bg: "#1c1917", border: "#d97706", text: "#fcd34d" },
    "Gastric Motility Agent": {
      bg: "#1c1917",
      border: "#a16207",
      text: "#fef9c3",
    },
    Laxative: { bg: "#052e16", border: "#34d399", text: "#a7f3d0" },
    NSAID: { bg: "#2d1f0e", border: "#fb923c", text: "#fed7aa" },
  };

// ══════════════════════════════════════════════════════════════════════
// ── Pathway category filter colors
// ══════════════════════════════════════════════════════════════════════
const PATHWAY_CAT_COLORS: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  Inflammation: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b" },
  "Adrenergic Receptors": { bg: "#dbeafe", border: "#3b82f6", text: "#1e3a5f" },
  "Neurotransmitter Inactivation": {
    bg: "#f0fdfa",
    border: "#14b8a6",
    text: "#134e4a",
  },
  "Cholinergic Receptors": {
    bg: "#f0fdf4",
    border: "#22c55e",
    text: "#14532d",
  },
  "Corticosteroid Production": {
    bg: "#fefce8",
    border: "#eab308",
    text: "#713f12",
  },
  "Muscarinic Receptors": { bg: "#fdf2f8", border: "#ec4899", text: "#831843" },
  "Glucocorticoid Anti-inflammatory": {
    bg: "#ede9fe",
    border: "#8b5cf6",
    text: "#4c1d95",
  },
  "Histamine Receptors": { bg: "#fff1f2", border: "#f87171", text: "#881337" },
  Antiarrhythmic: { bg: "#1e3a5f", border: "#60a5fa", text: "#bfdbfe" },
  Vasopressor: { bg: "#450a0a", border: "#f87171", text: "#fee2e2" },
  Inodilator: { bg: "#2e1065", border: "#c084fc", text: "#e9d5ff" },
  Vasodilator: { bg: "#042f2e", border: "#2dd4bf", text: "#99f6e4" },
  "Pulmonary Vasodilator": {
    bg: "#082f49",
    border: "#7dd3fc",
    text: "#e0f2fe",
  },
  Diuretic: { bg: "#052e16", border: "#4ade80", text: "#bbf7d0" },
  "Blood Product": { bg: "#3b0a0a", border: "#f87171", text: "#fecaca" },
  "Blood Products": { bg: "#3b0a0a", border: "#f87171", text: "#fecaca" },
};

const getPathwayColor = (cat: string) =>
  PATHWAY_CAT_COLORS[cat] || {
    bg: "#f3f4f6",
    border: "#6b7280",
    text: "#374151",
  };

const getColor = (cat: string) =>
  CAT_COLORS[cat] || { bg: "#f3f4f6", border: "#6b7280", text: "#374151" };

// ── Pipe-delimited field renderer ──
// Used for notes and primaryAction fields.
// Splits on "|", strips leading "•" bullets, renders as list or plain text.
const renderPipeField = (text: string, style: React.CSSProperties = {}) => {
  if (!text) return null;
  const parts = text
    .split("|")
    .map((s) => s.trim().replace(/^•\s*/, ""))
    .filter(Boolean);
  if (parts.length <= 1) {
    return <span style={style}>{parts[0] || text}</span>;
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 16, ...style }}>
      {parts.map((p, i) => (
        <li key={i} style={{ marginBottom: 3 }}>
          {p}
        </li>
      ))}
    </ul>
  );
};

// Keep renderNotes as alias for backward compat
const renderNotes = renderPipeField;

// ── Shuffle ──
const shuffle = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ══════════════════════════════════════════════════════════════════════
// ── Pathways Data
// ══════════════════════════════════════════════════════════════════════
// steps: pipe-delimited list of "molecule:text" or "arrow:↑/↓" tokens
// distractors: array of wrong answers for the word bank
// drugPrompt: if set, student must also name drug(s) after placing steps
// acceptCount: how many drug answers are required (0 = no drug question)
// drugAnswers: array of accepted answers (generic + trade)
const PATHWAYS: any[] = (pathwayData as any[])
  .flat(Infinity)
  .filter(
    (pw: any) => pw && typeof pw.id === "string" && typeof pw.steps === "string"
  );

const DRUG_TO_PATHWAY_ID: Record<string, string> = {
  acetaminophen: "selective_cox_inhibitor",
  acetazolamide: "acetazolamide_met_alkalosis",
  aclidinium: "lama",
  adenosine: "adenosine_svt",
  alteplase_rtpa: "recombinant_tissue_plasminogen_activator_rt_pa",
  aminoglycosides: "protein_synthesis_inhibitor",
  amiodarone: "amiodarone_erp",
  amlodipine: "ccb",
  amphotericin_b: "polyene_antifungal",
  atropine: "NSMblockade",
  benralizumab: "anti-IL5_c",
  benzocaine: "ester_type_local_anesthetic",
  bleomycin: "antineoplastic_chemotherapeutic",
  budesonide: "ics24",
  bupivacaine: "amide_type_local_anesthetic",
  caffeine: "caffeine",
  captopril: "ace_inhibitor_bp",
  carbapenems: "lactam",
  carfentanil: "opioid_agonist",
  cephalosporins: "lactam",
  cetirizine_hci: "gen2h1antihistamine",
  ciclesonide: "ics24",
  cimetidine: "h2antihistamine",
  cisatracurium: "ndnmb",
  clonidine: "clonidine",
  cocaine: "ester_type_local_anesthetic",
  cryoprecipitate: "cryoprecipitate_clotting",
  dabigatran: "direct_oral_anticoagulant_doac",
  dalteparin: "low_molecular_weight_heparin_lmwh",
  dexamethasone: "glucocorticoid",
  dexmedetomidine: "central_agonist",
  digoxin: "digoxin_cardiac_glycoside",
  diltiazem: "verapamil_diltiazem_av",
  diphenhydramine: "gen1h1antihistamine",
  dobutamine: "dobutamine_cardiogenic_shock",
  docusate: "docusate",
  dornase_alfa: "dornase_alfa",
  edrophonium: "edrophonium",
  enalapril: "ace_inhibitor_bp",
  enoxaparin: "low_molecular_weight_heparin_lmwh",
  epinephrine: "epinephrine_anaphylaxis",
  epoprostenol: "epoprostenol_pah",
  "esomeprazole*": "ppi_h_k_atpase",
  ethambutol: "first_line_tuberculosis_drug",
  etomidate: "gaba_potentiator",
  famotidine: "h2antihistamine",
  fentanyl: "opioid_agonist",
  flumazenil: "benzodiazepine_antagonist",
  fluoroquinolones: "dna_synthesis_inhibitor",
  fluticasone_furoate: "ics24",
  fluticasone_propionate: "ics12",
  formoterol: "laba",
  furosemide: "furosemide_diuresis",
  glycopyrrolate: "glycopyrrolate",
  glycopyrronium: "lama",
  haloperidol: "dopamine_d_serotonin_blocker",
  hydralazine: "hydralazine",
  hydrochlorothiazide_hctz: "hctz_diuresis",
  hydrocortisone: "glucocorticoid",
  hydromorphone: "opioid_agonist",
  hypertonic_saline_3_7: "expectorant",
  ibuprofen: "non_selective_reversible_cox_inhibitor",
  inhaled_nitric_oxide_ino: "ino_pathway",
  ipratropium_bromide: "sama",
  isoniazid: "first_line_tuberculosis_drug",
  isoproterenol: "isoproterenol",
  ketamine: "nmda_antagonist",
  labetalol: "labetalol_dual_blockade",
  lactulose: "lactulose",
  lidocaine: "lidocaine_ventricular",
  loratadine: "gen2h1antihistamine",
  lorazepam: "benzodiazepine",
  macrolides: "protein_synthesis_inhibitor",
  mannitol: "mannitol_icp",
  mepolizumab: "anti-IL5_f",
  methacholine: "methacholine",
  methotrimeprazine: "dopamine_d_serotonin_blocker",
  metoclopramide: "metoclopramide",
  metoprolol: "metoprolol_rate_control",
  midazolam: "benzodiazepine",
  milrinone: "milrinone_chf",
  montelukast: "ltra",
  morphine: "opioid_agonist",
  n_acetylcysteine: "n_acetylcysteine",
  naloxone: "opioid_antagonist",
  nebulized_epinephrine: "nebulized_epinephrine",
  neostigmine: "neostigmine",
  nitroglycerin: "nitroglycerin_angina",
  nitroprusside: "nitroprusside_htn_emergency",
  norepinephrine: "norepi_septic_shock",
  omalizumab: "anti-ige",
  oseltamivir: "neuraminidase_inhibitor",
  packed_red_blood_cells: "prbc_o2_delivery",
  "pancuronium*": "ndnmb",
  pantoprazole: "ppi_h_k_atpase",
  peg_lyte: "peg_lyte",
  penicillin: "lactam",
  pentamidine: "antiprotozoal_agent",
  phenobarbital: "barbiturate",
  phenylephrine: "phenylephrine_pure_alpha",
  prednisone: "glucocorticoid",
  propofol: "gaba_potentiator",
  protamine: "heparin_reversal_agent",
  pyrazinamide: "first_line_tuberculosis_drug",
  quetiapine: "dopamine_d_serotonin_blocker",
  ramipril: "ace_inhibitor_bp",
  remifentanil: "opioid_agonist",
  reslizumab: "anti-IL5_f",
  rifampin: "first_line_tuberculosis_drug",
  rivaroxaban: "direct_oral_anticoagulant_doac",
  rocuronium: "ndnmb",
  roflumilast: "roflumilast",
  salbutamol: "saba",
  salmeterol: "laba",
  scopolamine: "scopolamine",
  sildenafil: "sildenafil_pah",
  spironolactone: "spironolactone_k_sparing",
  succinylcholine: "succinylcholine",
  sufentanil: "opioid_agonist",
  sugammadex: "sugammadex",
  terbutaline: "saba",
  tetracyclines: "protein_synthesis_inhibitor",
  theophylline: "theophylline",
  tiotropium_bromide: "lama",
  tranexamic_acid_txa: "anti_fibrinolytic",
  umeclidinium: "lama",
  unfractionated_heparin_ufh:
    "indirect_anticoagulant_antithrombin_iii_activator",
  vancomycin: "glycopeptide_antibiotic",
  vasopressin: "vasopressin_v1_v2",
  verapamil: "verapamil_diltiazem_av",
  vilanterol: "laba",
  vitamin_k: "warfarin_reversal_agent",
  warfarin: "vitamin_k_antagonist",
  zafirlukast: "ltra",
};

// Parse steps string into renderable step objects.
// Token types:
//   molecule:text  → draggable fill slot (plain)
//   arrow:↑/↓      → when immediately followed by molecule:, collapses into a "paired" step
//                     with direction picker on left + molecule slot on right
//   label:text     → static non-draggable section divider (branch labels etc.)
type StepItem =
  | { type: "molecule"; text: string }
  | { type: "paired"; direction: "↑" | "↓"; text: string }
  | { type: "label"; text: string };

const parsePairedSteps = (stepsStr: string): StepItem[] => {
  const raw = stepsStr.split("|").map((token) => {
    const colon = token.indexOf(":");
    return { type: token.slice(0, colon), text: token.slice(colon + 1) };
  });
  const out: StepItem[] = [];
  let i = 0;
  while (i < raw.length) {
    const cur = raw[i];
    if (
      cur.type === "arrow" &&
      i + 1 < raw.length &&
      raw[i + 1].type === "molecule"
    ) {
      out.push({
        type: "paired",
        direction: cur.text as "↑" | "↓",
        text: raw[i + 1].text,
      });
      i += 2;
    } else if (cur.type === "molecule") {
      out.push({ type: "molecule", text: cur.text });
      i += 1;
    } else if (cur.type === "label") {
      out.push({ type: "label", text: cur.text });
      i += 1;
    } else {
      // standalone arrow with no following molecule — skip
      i += 1;
    }
  }
  return out;
};

// ══════════════════════════════════════════════════════════════════════
// ── Main App
// ══════════════════════════════════════════════════════════════════════
export default function PharmaStudy() {
  const [mode, setMode] = useState("home");
  const [showCascade, setShowCascade] = useState(false);
  const [selectedCats, setSelectedCats] = useState(new Set(CATEGORIES));

  const filtered = useMemo(
    () => DRUGS.filter((d) => selectedCats.has(d.category)),
    [selectedCats]
  );

  const toggleCat = (cat: string) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const selectAll = () => setSelectedCats(new Set(CATEGORIES));
  const selectNone = () => setSelectedCats(new Set());

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0f172a",
        color: "#e2e8f0",
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1a1a2e 100%)",
          borderBottom: "1px solid #334155",
          padding: "20px 24px",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              {/* Logo + name doubles as a "back to menu" link */}
              <div
                onClick={() => setMode("home")}
                title="Back to menu"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 4,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 36 }}>💊</span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 36,
                    color: "#38bdf8",
                    letterSpacing: 1,
                  }}
                >
                  PharmDrill
                </h1>
              </div>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#ddedf0" }}>
                Respiratory Pharmacology · Study Tools · 2026
              </p>
              {mode !== "home" && (
                <button
                  onClick={() => setMode("home")}
                  style={{
                    padding: "8px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 24,
                    border: "1px solid #64748b",
                    background: "transparent",
                    color: "#ffffff",
                    cursor: "pointer",
                    marginTop: 10,
                  }}
                >
                  ← Menu
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <button
                    onClick={() => setShowCascade(!showCascade)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 20px",
                      fontSize: 14,
                      fontWeight: "bold",
                      borderRadius: 24,
                      border: "1px solid",
                      borderColor: showCascade ? "#fb923c" : "#86efac",
                      background: showCascade
                        ? "rgba(251, 146, 60, 0.1)"
                        : "rgba(134, 239, 172, 0.1)",
                      color: showCascade ? "#fb923c" : "#86efac",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: 18 }}>
                      {showCascade ? "⛓" : "⚡"}
                    </span>
                    <span>{showCascade ? "Full Cascade" : "Simple Mode"}</span>
                  </button>
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: "#ffffff",
                    maxWidth: 300,
                    textAlign: "right",
                    lineHeight: 1.4,
                  }}
                >
                  Toggle the button above to switch between simple class/action
                  to view full signaling pathways
                </div>
              </div>
            </div>
          </div>
          <DrugLookup showCascade={showCascade} />
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 16px" }}>
        {mode === "home" && (
          <HomeScreen
            mode={mode}
            setMode={setMode}
            selectedCats={selectedCats}
            toggleCat={toggleCat}
            selectAll={selectAll}
            selectNone={selectNone}
            drugCount={filtered.length}
          />
        )}
        {mode === "flashcards" && (
          <Flashcards drugs={filtered} showCascade={showCascade} />
        )}
        {mode === "match" && (
          <MatchGame drugs={filtered} showCascade={showCascade} />
        )}
        {mode === "namematch" && <NameMatchGame drugs={filtered} />}
        {mode === "inhaler-namematch" && (
          <NameMatchGame drugs={INHALER_DRUGS} includeNoTrade />
        )}
        {mode === "inhaler-devices" && <InhalerDeviceMode />}
        {mode === "pathway" && <PathwayMode />}
        {mode === "hemo" && <HemodynamicsMode />}
        {/* ── NEW: Mock Exam Section ── */}
        {mode === "mock-exams" && (
          <div
            style={{
              maxWidth: 800,
              margin: "0 auto",
              animation: "fadeIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: 24,
                background: "linear-gradient(145deg, #1e293b, #0f172a)",
                borderRadius: 16,
                border: "1px solid #38bdf8",
                marginBottom: 24,
              }}
            >
              <h2
                style={{
                  color: "#38bdf8",
                  marginTop: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span>📢</span> Announcements
              </h2>
              <p style={{ color: "#e2e8f0", fontSize: 15, lineHeight: 1.6 }}>
                The final mock exam version A & B are both now available. Take them as many times as you'd like!
              </p>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <h3
                style={{
                  color: "#94a3b8",
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Testmoz Mock Exams
              </h3>
              {/* First Button */}
              <a
                href="https://testmoz.com/q/15349114"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "20px",
                  background: "#1e293b",
                  borderRadius: 12,
                  color: "#fff",
                  textDecoration: "none",
                  border: "1px solid #334155",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateX(5px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateX(0)")
                }
              >
                <div>
                  <div style={{ fontWeight: "bold", fontSize: 16 }}>
                    Respiratory Pharmacology FINAL MOCK EXAM VERSION A
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>Enjoy.</div>
                </div>
                <span style={{ color: "#38bdf8", fontWeight: "bold" }}>
                  Start Exam →
                </span>
              </a>

              {/* Second Button */}
              <a
                href="https://testmoz.com/q/15349118"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "20px",
                  background: "#1e293b",
                  borderRadius: 12,
                  color: "#fff",
                  textDecoration: "none",
                  border: "1px solid #334155",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateX(5px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateX(0)")
                }
              >
                <div>
                  <div style={{ fontWeight: "bold", fontSize: 16 }}>
                    Respiratory Pharmacology FINAL MOCK EXAM VERSION B
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    (Slightly more focused on coagulation-related drugs)
                  </div>
                </div>
                <span style={{ color: "#38bdf8", fontWeight: "bold" }}>
                  Start Exam →
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
      <Analytics />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Drug Lookup (header search bar)
// ══════════════════════════════════════════════════════════════════════
// Reuses the same flattened DRUGS list and renderPipeField helper as the
// study modes. Matches the typed query against generic name, trade name,
// class label, and category, then shows full detail cards in a dropdown.
// Respects the global showCascade toggle (cascade vs. primary action),
// exactly like the Flashcards answer side.
function DrugLookup({ showCascade }: { showCascade: boolean }) {
  const [query, setQuery] = useState("");
  // Which result row is expanded. Only one open at a time keeps the list
  // short and scannable on small (phone) screens.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Shared style for the small uppercase field labels in the expanded detail.
  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 2,
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return DRUGS.filter((d) => {
      const haystack = [d.generic, d.trade || "", d.classLabel, d.category]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    }).slice(0, 25);
  }, [query]);

  const formatName = (d: DrugEntry) =>
    d.trade && d.trade !== "N/A" ? `${d.trade} (${d.generic})` : d.generic;

  return (
    <div style={{ position: "relative", marginTop: 14 }}>
      {/* Input row */}
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 15,
            pointerEvents: "none",
          }}
        >
          🔍
        </span>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setExpandedId(null);
          }}
          placeholder="Look up any drug by name, class, or category…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "11px 38px 11px 38px",
            fontSize: 16,
            borderRadius: 24,
            border: "1px solid #334155",
            background: "#0b1220",
            color: "#e2e8f0",
            outline: "none",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#38bdf8")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "#334155")}
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setExpandedId(null);
            }}
            aria-label="Clear search"
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {query.trim() && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            maxHeight: "min(70vh, 460px)",
            overflowY: "auto",
            background: "#0f172a",
            border: "1px solid #334155",
            borderRadius: 14,
            zIndex: 200,
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            padding: 8,
          }}
        >
          {results.length === 0 ? (
            <div
              style={{
                padding: 16,
                color: "#94a3b8",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              No drugs found for "{query.trim()}".
            </div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: "#64748b", padding: "4px 8px 8px" }}>
                {results.length === 25 ? "25+" : results.length} match
                {results.length === 1 ? "" : "es"}
                {results.length === 25 ? " — refine to narrow" : ""} · tap to
                expand
              </div>
              {results.map((d) => {
                const c = getColor(d.category);
                const open = expandedId === d.id;
                return (
                  <div key={d.id} style={{ marginBottom: 6 }}>
                    {/* Compact tappable row */}
                    <button
                      onClick={() => setExpandedId(open ? null : d.id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: "#1e293b",
                        border: `1px solid ${open ? c.border : "#334155"}`,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: c.border,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#e2e8f0",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {formatName(d)}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          color: c.text,
                          background: c.bg,
                          border: `1px solid ${c.border}`,
                          borderRadius: 10,
                          padding: "2px 8px",
                          whiteSpace: "nowrap",
                          maxWidth: "40%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flexShrink: 0,
                        }}
                      >
                        {d.category}
                      </span>
                      <span
                        style={{ color: "#64748b", fontSize: 10, flexShrink: 0 }}
                      >
                        {open ? "▲" : "▼"}
                      </span>
                    </button>

                    {/* Expanded detail (only the open row) */}
                    {open && (
                      <div
                        style={{
                          padding: 12,
                          marginTop: 4,
                          borderRadius: 10,
                          background: "#162032",
                          border: "1px solid #334155",
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>Class</div>
                          <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                            {d.classLabel}
                          </div>
                        </div>

                        <div style={{ marginBottom: d.notes ? 8 : 0 }}>
                          <div style={labelStyle}>
                            {showCascade
                              ? "Signaling Cascade"
                              : "Primary Action"}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#38bdf8",
                              lineHeight: 1.5,
                            }}
                          >
                            {showCascade
                              ? d.cascade
                              : renderPipeField(d.primaryAction, {
                                  fontSize: 13,
                                  color: "#38bdf8",
                                  lineHeight: 1.5,
                                })}
                          </div>
                        </div>

                        {d.notes && (
                          <div
                            style={{
                              borderTop: "1px solid #334155",
                              paddingTop: 8,
                            }}
                          >
                            <div style={labelStyle}>Notes</div>
                            {renderPipeField(d.notes, {
                              fontSize: 12,
                              color: "#cbd5e1",
                              lineHeight: 1.5,
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Home Screen
// ══════════════════════════════════════════════════════════════════════
// No emoji exists for an inhaler, so the MDI menu icon is drawn inline
// (sized in em so it matches the emoji icons)
function MdiIcon() {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 32 32"
      style={{ verticalAlign: "middle" }}
      aria-label="MDI puffer"
    >
      {/* Canister */}
      <rect x="16" y="1" width="8" height="15" rx="2" fill="#cbd5e1" />
      <rect x="16" y="1" width="8" height="3" rx="1.5" fill="#94a3b8" />
      {/* Boot + mouthpiece */}
      <path
        d="M14 10 h12 v18 a3 3 0 0 1 -3 3 H10 a3 3 0 0 1 -3 -3 v-4 a3 3 0 0 1 3 -3 h4 Z"
        fill="#3b82f6"
      />
      <rect x="7" y="22" width="2" height="7" rx="1" fill="#1e3a8a" />
      {/* Puff */}
      <circle cx="4" cy="22" r="1.4" fill="#94a3b8" />
      <circle cx="2" cy="25.5" r="1.2" fill="#94a3b8" />
      <circle cx="4" cy="29" r="1" fill="#94a3b8" />
    </svg>
  );
}

function HomeScreen({
  setMode,
  selectedCats,
  toggleCat,
  selectAll,
  selectNone,
  drugCount,
}: any) {
  const [showCats, setShowCats] = React.useState(false);
  // Modes that don't use the category filter stay enabled with 0 drugs selected
  const filterFree = (key: string) =>
    key === "hemo" ||
    key === "pathway" ||
    key === "inhaler-namematch" ||
    key === "inhaler-devices";
  const modes = [
    {
      key: "flashcards",
      icon: "🗂️",
      title: "Smart Flashcards",
      desc: "'Still Learning' cards will be shown again at end of pile until you 'Got it' (progress saved to device browser)",
    },
    {
      key: "match",
      icon: "🔗",
      title: "Match Game",
      desc: "Pair drugs with their mechanisms",
    },
    {
      key: "namematch",
      icon: "🏷️",
      title: "Name Match",
      desc: "Match trade name, generic name, and drug class",
    },
    {
      key: "inhaler-namematch",
      icon: "🫁",
      title: "Inhaler Name Match",
      desc: "Name Match with inhalers only (SABA, SAMA, LABA, LAMA, ICS, Combo), no category filter needed",
    },
    {
      key: "inhaler-devices",
      icon: <MdiIcon />,
      title: "Inhaler Devices & Doses",
      desc: "Select which devices each inhaler comes in, then quiz dose, dosage order, and duration",
    },
    {
      key: "pathway",
      icon: "🧬",
      title: "Pathway Builder",
      desc: "Reconstruct signaling cascades step by step",
    },
    {
      key: "hemo",
      icon: "🫀",
      title: "Case Study Work Flow",
      desc: "MAP → Tank → Pipes → Pump decision flow",
    },
    {
      key: "mock-exams",
      icon: "📝",
      title: "Mock Exams",
      desc: "Access timed practice exams",
    },
  ];

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px 40px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: showCats ? 12 : 24,
        }}
      >
        <button
          onClick={() => setShowCats((v) => !v)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 0,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 15,
              color: "#e2e8f0",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Drug Categories ({drugCount} drugs selected)
          </h3>
          <span style={{ color: "#64748b", fontSize: 13 }}>
            {showCats ? "▲" : "▼"} expand to filter by categories
          </span>
        </button>

        {showCats && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #ffffff",
              borderRadius: 20,
              padding: "4px 12px",
            }}
          >
            <button
              onClick={selectAll}
              style={{
                fontSize: 13,
                fontWeight: "bold",
                color: "#38bdf8",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              All
            </button>
            <div
              style={{
                width: 1,
                height: 14,
                background: "rgba(255,255,255,0.3)",
              }}
            />
            <button
              onClick={selectNone}
              style={{
                fontSize: 13,
                fontWeight: "bold",
                color: "#f87171",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              None
            </button>
          </div>
        )}
      </div>

      {showCats && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 24,
          }}
        >
          {CATEGORIES.map((cat) => {
            const active = selectedCats.has(cat);
            const c = getColor(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCat(cat)}
                style={{
                  padding: "6px 14px",
                  fontSize: 12,
                  borderRadius: 16,
                  border: `1.5px solid ${active ? c.border : "#334155"}`,
                  background: active ? c.bg : "transparent",
                  color: active ? c.text : "#94a3b8",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: active ? 700 : 500,
                  opacity: active ? 1 : 0.7,
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        {modes.map((m) => (
          <button
            key={m.key}
            onClick={() => (filterFree(m.key) || drugCount > 0) && setMode(m.key)}
            style={{
              padding: 24,
              borderRadius: 14,
              border: "1px solid #1e293b",
              background: "linear-gradient(145deg, #1e293b, #162032)",
              cursor: filterFree(m.key) || drugCount > 0 ? "pointer" : "not-allowed",
              textAlign: "left",
              transition: "all 0.25s",
              opacity: filterFree(m.key) || drugCount > 0 ? 1 : 0.4,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor =
                "#38bdf8";
              (e.currentTarget as HTMLButtonElement).style.transform =
                "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor =
                "#1e293b";
              (e.currentTarget as HTMLButtonElement).style.transform =
                "translateY(0)";
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>{m.icon}</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e2e8f0",
                marginBottom: 4,
              }}
            >
              {m.title}
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>{m.desc}</div>
          </button>
        ))}
      </div>

      <p
        style={{
          marginTop: 32,
          fontSize: 12,
          color: "#faf8f7",
          textAlign: "center",
        }}
      >
        Concept and drug database by Cherry Fan, compiled entirely from
        human-authored sources. Code co-written with Claude Code.
        <br />
        Respiratory Pharmacology | Thompson Rivers University | 2026 | fanc16@mytru.ca
      </p>
      {/* Disclaimer Section */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: "1px solid rgba(255, 212, 248, 0.2)",
          textAlign: "center",
          maxWidth: "700px",
          margin: "16px auto 0",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            color: "#ffd4f8",
            lineHeight: "1.6",
            margin: 0,
          }}
        >
          <strong>Disclaimer:</strong> This webapp is a student-led study
          project. While every effort is made to ensure accuracy, the content
          may contain errors and is not a substitute for course contents. Please
          cross-reference with official course materials and consult your
          instructor for final confirmation. Any verified discrepancies can be
          notified to email address above for correction.
        </p>
        <p
          style={{
            fontSize: "12px",
            color: "#ffd4f8",
            marginTop: 8,
            fontStyle: "italic",
          }}
        >
          * Note: Optional knowledge/contents are marked with an asterisk (*).
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Flashcards (Browser Memory Edition)
// ══════════════════════════════════════════════════════════════════════
function Flashcards({
  drugs,
  showCascade,
}: {
  drugs: DrugEntry[];
  showCascade: boolean;
}) {
  const [knownDrugs, setKnownDrugs] = useState<string[]>(() => {
    const saved = localStorage.getItem("pharmDrill_knownDrugs");
    return saved ? JSON.parse(saved) : [];
  });

  const [deck, setDeck] = useState<DrugEntry[]>([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const toStudy = drugs.filter((d) => !knownDrugs.includes(d.id));
    setDeck(shuffle(toStudy));
    setIdx(0);
    setFlipped(false);
  }, [drugs]);

  const card = deck[idx];

  const handleGotIt = () => {
    const updatedKnown = [...knownDrugs, card.id];
    setKnownDrugs(updatedKnown);
    localStorage.setItem("pharmDrill_knownDrugs", JSON.stringify(updatedKnown));
    setFlipped(false);
    setTimeout(() => setIdx((i) => i + 1), 150);
  };

  const handleStillLearning = () => {
    setFlipped(false);
    setTimeout(() => {
      setDeck((prevDeck) => [...prevDeck, card]);
      setIdx((i) => i + 1);
    }, 150);
  };

  const resetProgress = () => {
    if (
      window.confirm(
        "Are you sure? This will wipe your memory for all categories!"
      )
    ) {
      setKnownDrugs([]);
      localStorage.removeItem("pharmDrill_knownDrugs");
      setDeck(shuffle(drugs));
      setIdx(0);
      setFlipped(false);
    }
  };

  const formatName = (d: DrugEntry) =>
    d.trade && d.trade !== "N/A" ? `${d.trade} (${d.generic})` : d.generic;

  if (idx >= deck.length && deck.length > 0) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2 style={{ color: "#e2e8f0", marginBottom: 8 }}>All Caught Up!</h2>
        <p style={{ color: "#94a3b8", marginBottom: 24, lineHeight: 1.5 }}>
          You have mastered all {drugs.length} drugs in this category.
          <br />
          Your progress is saved to this browser.
        </p>
        <button
          onClick={resetProgress}
          style={{
            padding: "10px 28px",
            borderRadius: 10,
            background: "#b91c1c",
            color: "#fca5a5",
            border: "1px solid #7f1d1d",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Wipe Memory & Restart
        </button>
      </div>
    );
  }

  if (!card) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <h2 style={{ color: "#e2e8f0", marginBottom: 8 }}>Nothing to study!</h2>
        <p style={{ color: "#94a3b8", marginBottom: 24 }}>
          You either haven't selected any categories, or you've already mastered
          them all!
        </p>
        {knownDrugs.length > 0 && (
          <button
            onClick={resetProgress}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              background: "transparent",
              border: "1px solid #475569",
              color: "#94a3b8",
              cursor: "pointer",
            }}
          >
            Reset Progress
          </button>
        )}
      </div>
    );
  }

  const c = getColor(card.category);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          fontSize: 12,
          color: "#94a3b8",
        }}
      >
        <span>Cards Remaining: {deck.length - idx}</span>
        <button
          onClick={resetProgress}
          style={{
            background: "none",
            border: "none",
            color: "#ef4444",
            cursor: "pointer",
            fontSize: 11,
          }}
        >
          Reset All Progress
        </button>
      </div>

      <div
        onClick={() => setFlipped(!flipped)}
        style={{
          minHeight: 260,
          padding: 28,
          borderRadius: 16,
          background: flipped
            ? "#01132b"
            : "linear-gradient(145deg, #1e293b, #162032)",
          border: `1.5px solid ${flipped ? c.border : "#334155"}`,
          cursor: "pointer",
          transition: "all 0.3s",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            fontSize: 10,
            color: "#94a3b8",
          }}
        >
          {flipped ? "ANSWER" : "TAP TO FLIP"}
        </div>

        {!flipped ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: 200,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: "#e8e8e8",
                marginBottom: 16,
              }}
            >
              {formatName(card)}
            </div>
            <div style={{ fontSize: 13, color: "#d4c1c6", lineHeight: 1.8 }}>
              <div>What is the mode of action?</div>
              <div>What is used to primarily treat?</div>
              <div>What are some possible side effects?</div>
              <div>Any interaction with other drugs?</div>
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 12,
                  background: c.bg,
                  color: c.text,
                  fontSize: 11,
                  fontWeight: 600,
                  border: `1px solid ${c.border}`,
                }}
              >
                {card.category}
              </span>
              <span style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0" }}>
                {formatName(card)}
              </span>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#e1faf7",
                  marginBottom: 3,
                  textTransform: "uppercase",
                }}
              >
                Class
              </div>
              <div style={{ fontSize: 14, color: "#ccdce3", fontWeight: 500 }}>
                {card.classLabel}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#ccdce3",
                  marginBottom: 3,
                  textTransform: "uppercase",
                }}
              >
                {showCascade ? "Signaling Cascade" : "Primary Action"}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#38bdf8",
                  lineHeight: 1.5,
                  fontWeight: 500,
                }}
              >
                {showCascade
                  ? card.cascade
                  : renderPipeField(card.primaryAction, {
                      fontSize: 14,
                      color: "#38bdf8",
                      lineHeight: 1.5,
                      fontWeight: 500,
                    })}
              </div>
            </div>
            {card.notes && (
              <div
                style={{
                  fontSize: 12,
                  color: "#cbd5e1",
                  lineHeight: 1.5,
                  borderTop: "1px solid #334155",
                  paddingTop: 10,
                  marginTop: 10,
                }}
              >
                {renderPipeField(card.notes, {
                  fontSize: 12,
                  color: "#fcf3f2",
                  lineHeight: 1.5,
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {flipped && (
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStillLearning();
            }}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 10,
              background: "#7f1d1d",
              color: "#fca5a5",
              border: "1px solid #991b1b",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ✕ Still Learning
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleGotIt();
            }}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 10,
              background: "#14532d",
              color: "#86efac",
              border: "1px solid #166534",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ✓ Got It
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Match Game
// ══════════════════════════════════════════════════════════════════════
function MatchGame({
  drugs,
  showCascade,
}: {
  drugs: DrugEntry[];
  showCascade: boolean;
}) {
  const ROUND_SIZE = 5;
  const [round, setRound] = useState<DrugEntry[]>([]);
  const [mechanisms, setMechanisms] = useState<DrugEntry[]>([]);
  const [selectedDrug, setSelectedDrug] = useState<string | null>(null);
  const [matched, setMatched] = useState(new Set<string>());
  const [wrongPair, setWrongPair] = useState<{
    drugId: string;
    mechId: string;
  } | null>(null);
  const [score, setScore] = useState({ correct: 0, attempts: 0 });

  const startRound = useCallback(() => {
    const picks = shuffle(drugs).slice(0, Math.min(ROUND_SIZE, drugs.length));
    setRound(picks);
    setMechanisms(shuffle([...picks]));
    setSelectedDrug(null);
    setMatched(new Set());
    setWrongPair(null);
  }, [drugs]);

  useEffect(() => {
    startRound();
  }, [startRound]);

  const nameDisplay = useMemo(() => {
    const map: Record<string, string> = {};
    round.forEach((d) => {
      map[d.id] =
        d.trade && d.trade !== "N/A"
          ? Math.random() > 0.5
            ? "generic"
            : "trade"
          : "generic";
    });
    return map;
  }, [round]);

  const getDrugLabel = (drug: DrugEntry) =>
    nameDisplay[drug.id] === "generic"
      ? drug.generic
      : drug.trade || drug.generic;

  const handleMechClick = (mechId: string) => {
    if (!selectedDrug || matched.has(mechId)) return;

    setScore((s) => ({ ...s, attempts: s.attempts + 1 }));

    if (mechId === selectedDrug) {
      setMatched((prev) => new Set([...prev, mechId]));
      setScore((s) => ({ ...s, correct: s.correct + 1 }));
      setSelectedDrug(null);
      setWrongPair(null);
      return;
    }

    setWrongPair({ drugId: selectedDrug, mechId });
    setSelectedDrug(null);
    setTimeout(() => setWrongPair(null), 700);
  };

  const allMatched = matched.size === round.length && round.length > 0;
  const availableDrugs = round.filter((d) => !matched.has(d.id));

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 14, color: "#cbd5e1" }}>
            Match drug to mechanism
          </h3>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
            Tap a drug from the Drug Bank at bottom of page, then tap the
            matching card.
          </div>
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
          {score.correct}/{score.attempts} correct
        </span>
      </div>

      {allMatched ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "#93c5fd",
              marginBottom: 16,
            }}
          >
            Round Complete
          </div>
          <h2 style={{ color: "#e2e8f0" }}>Round Complete!</h2>
          <p style={{ color: "#94a3b8", marginBottom: 20 }}>
            {score.correct}/{score.attempts} correct attempts
          </p>
          <button
            onClick={startRound}
            style={{
              padding: "10px 28px",
              borderRadius: 10,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            New Round
          </button>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 220,
            }}
          >
            {mechanisms.map((drug) => {
              const done = matched.has(drug.id);
              const isWrong = wrongPair?.mechId === drug.id;
              const isReady = !!selectedDrug && !done;
              return (
                <button
                  key={drug.id}
                  onClick={() => handleMechClick(drug.id)}
                  disabled={done}
                  style={{
                    width: "100%",
                    padding: 16,
                    borderRadius: 10,
                    background: "#1e293b",
                    border: `1px solid ${
                      done
                        ? "#22c55e"
                        : isWrong
                        ? "#ef4444"
                        : isReady
                        ? "#60a5fa"
                        : "#334155"
                    }`,
                    marginBottom: 8,
                    textAlign: "left",
                    cursor: done
                      ? "default"
                      : selectedDrug
                      ? "pointer"
                      : "default",
                    color: "#e2e8f0",
                    boxShadow: isReady
                      ? "0 0 0 1px rgba(96,165,250,0.2)"
                      : "none",
                    transition:
                      "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
                  }}
                >
                  {!showCascade && (
                    <div
                      style={{
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        color: "#93c5fd",
                        marginBottom: 6,
                      }}
                    >
                      {drug.classLabel}
                    </div>
                  )}
                  <div
                    style={{ fontSize: 14, lineHeight: 1.5, color: "#e2e8f0" }}
                  >
                    {(showCascade ? drug.cascade : drug.primaryAction)
                      .split("|")
                      .map((line, i) => (
                        <div key={i}>{line.trim()}</div>
                      ))}
                  </div>
                  {done && (
                    <div
                      style={{
                        marginTop: 10,
                        color: "#22c55e",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {"\u2713"} {getDrugLabel(drug)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "#0e116b",
              borderTop: "1px solid #334155",
              padding: "12px 16px",
              zIndex: 100,
            }}
          >
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#e3e884",
                  marginBottom: 10,
                }}
              >
                DRUG BANK
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {availableDrugs.map((drug) => {
                  const isSelected = selectedDrug === drug.id;
                  const isWrong = wrongPair?.drugId === drug.id;
                  return (
                    <button
                      key={drug.id}
                      onClick={() =>
                        setSelectedDrug((current) =>
                          current === drug.id ? null : drug.id
                        )
                      }
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        borderRadius: 999,
                        background: isWrong
                          ? "#7f1d1d"
                          : isSelected
                          ? "#2563eb"
                          : "#334155",
                        color: isSelected ? "#fff" : "#ffffff",
                        padding: "8px 14px",
                        fontSize: 14,
                        fontWeight: 700,
                        border: "none",
                        cursor: "pointer",
                        transition: "background 0.15s ease",
                      }}
                    >
                      {getDrugLabel(drug)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
type SlotValue = string | null | { mol: string | null; dir: "↑" | "↓" | null };

function PathwayMode() {
  const [pathway, setPathway] = useState<any>(null);
  const [phase, setPhase] = useState("select");
  const [subMode, setSubMode] = useState("practice");
  const [currentDrug, setCurrentDrug] = useState<any>(null);
  const [pathwayOptions, setPathwayOptions] = useState<any[]>([]);
  const [slots, setSlots] = useState<SlotValue[]>([]);
  const [bank, setBank] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [errorFeedback, setErrorFeedback] = useState<any>(null);

  // 1. START TEST MODE — only picks drugs that have a pathway mapped
  const startTestMode = () => {
    const mappableDrugs = DRUGS.filter((d) => DRUG_TO_PATHWAY_ID[d.id]);
    if (mappableDrugs.length === 0) return;
    const drug =
      mappableDrugs[Math.floor(Math.random() * mappableDrugs.length)];
    const useTrade = drug.trade && drug.trade !== "N/A" && Math.random() > 0.5;

    const correctPathwayId = DRUG_TO_PATHWAY_ID[drug.id];
    const correctPathway =
      PATHWAYS.find((p) => p.id === correctPathwayId) || PATHWAYS[0];
    const otherPathways = shuffle(
      PATHWAYS.filter((p) => p.id !== correctPathwayId)
    ).slice(0, 2);

    setCurrentDrug({
      ...drug,
      displayName: useTrade ? drug.trade! : drug.generic,
      correctPathwayId,
    });
    setPathwayOptions(shuffle([correctPathway, ...otherPathways]));
    setPhase("pick-pathway");
    setErrorFeedback(null);
  };

  // 2. VERIFY PATHWAY CHOICE — match by pathway id, not category string
  const handlePathwayChoice = (chosenPw: any) => {
    if (chosenPw.id === currentDrug.correctPathwayId) {
      setErrorFeedback(null);
      setupBuildPhase(chosenPw);
    } else {
      const example = DRUGS.find(
        (d) => DRUG_TO_PATHWAY_ID[d.id] === chosenPw.id
      );
      setErrorFeedback({
        msg: `Incorrect. That pathway is for the ${chosenPw.category} class.`,
        example: `Example: ${
          example ? example.generic : "Other drugs in this class"
        }.`,
        hint: `For ${currentDrug.displayName}, you need the ${currentDrug.category} pathway.`,
      });
    }
  };

  const setupBuildPhase = (pw: any) => {
    const steps = parsePairedSteps(pw.steps);
    const correct = steps.filter((s) => s.type !== "label").map((s) => s.text);
    const distractors = Array.isArray(pw.distractors) ? pw.distractors : [];

    // Labels are static — only molecule and paired steps get slots.
    // We keep a parallel slot array aligned to steps by index;
    // label indices get null and are never interacted with.
    const initSlots: SlotValue[] = steps.map((s) =>
      s.type === "paired" ? { mol: null, dir: null } : null
    );

    setPathway(pw);
    setSlots(initSlots);
    setBank(shuffle([...correct, ...distractors]));
    setPhase("build");
    setChecked(false);
  };

  // Place selected bank item into a molecule slot
  const handleSlotClick = (idx: number) => {
    if (checked) return;
    const slot = slots[idx];
    const isPaired = slot !== null && typeof slot === "object";
    const isPlain = !isPaired;

    if (isPaired) {
      const s = slot as { mol: string | null; dir: "↑" | "↓" | null };
      if (s.mol) {
        // Return existing molecule to bank, clear mol
        setBank(shuffle([...bank, s.mol]));
        setSlots((prev) => {
          const n = [...prev];
          n[idx] = { mol: null, dir: s.dir };
          return n;
        });
      } else if (selected) {
        setSlots((prev) => {
          const n = [...prev];
          n[idx] = { mol: selected, dir: s.dir };
          return n;
        });
        setBank(bank.filter((b) => b !== selected));
        setSelected(null);
      }
    } else {
      // Plain molecule slot
      if (slot !== null) {
        setBank(shuffle([...bank, slot as string]));
        setSlots((prev) => {
          const n = [...prev];
          n[idx] = null;
          return n;
        });
      } else if (selected) {
        setSlots((prev) => {
          const n = [...prev];
          n[idx] = selected;
          return n;
        });
        setBank(bank.filter((b) => b !== selected));
        setSelected(null);
      }
    }
  };

  // Toggle direction on a paired slot
  const handleDirToggle = (idx: number, dir: "↑" | "↓") => {
    if (checked) return;
    setSlots((prev) => {
      const n = [...prev];
      const s = n[idx] as { mol: string | null; dir: "↑" | "↓" | null };
      n[idx] = { ...s, dir: s.dir === dir ? null : dir };
      return n;
    });
  };

  // Labels are static — always "correct" (no student input required).
  // Paired slots require both correct molecule AND correct direction.
  // Plain molecule slots require matching text.
  const isSlotCorrect = (step: StepItem, slot: SlotValue): boolean => {
    if (step.type === "label") return true;
    if (step.type === "paired") {
      const s = slot as { mol: string | null; dir: "↑" | "↓" | null };
      return s?.mol === step.text && s?.dir === step.direction;
    }
    return slot === step.text;
  };

  const checkBuild = () => {
    setChecked(true);
  };

  if (phase === "select") {
    return (
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <button
            onClick={() => setSubMode("practice")}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 10,
              border: "1px solid #334155",
              background: subMode === "practice" ? "#1e3a4a" : "#1e293b",
              color: subMode === "practice" ? "#38bdf8" : "#94a3b8",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            📋 Practice
          </button>
          <button
            onClick={() => setSubMode("test")}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 10,
              border: "1px solid #334155",
              background: subMode === "test" ? "#1e3a4a" : "#1e293b",
              color: subMode === "test" ? "#38bdf8" : "#94a3b8",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            🎲 Test Mode
          </button>
        </div>

        {subMode === "practice" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {PATHWAYS.map((pw) => (
              <button
                key={pw.id}
                onClick={() => setupBuildPhase(pw)}
                style={{
                  padding: 15,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 12,
                  color: "#fff",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: 14 }}>
                  {(pw as any).title || `${pw.category} Pathway`}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {pw.id.replace(/_/g, " ")}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              background: "#1e293b",
              borderRadius: 16,
            }}
          >
            <div style={{ fontSize: 50, marginBottom: 15 }}>🎯</div>
            <h2 style={{ color: "#fff" }}>Random Drug Challenge</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 20 }}>
              A random drug is picked from drugs with a mapped pathway.
              <br />
              Identify the correct pathway, then build it step by step.
            </p>
            <button
              onClick={startTestMode}
              style={{
                padding: "12px 30px",
                borderRadius: 25,
                background: "#38bdf8",
                color: "#0f172a",
                border: "none",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Start Challenge
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === "pick-pathway") {
    return (
      <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ color: "#38bdf8" }}>Drug: {currentDrug.displayName}</h2>
        <p style={{ color: "#94a3b8", marginBottom: 20 }}>
          Choose the correct signaling cascade template:
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pathwayOptions.map((pw: any) => (
            <button
              key={pw.id}
              onClick={() => handlePathwayChoice(pw)}
              style={{
                padding: 16,
                background: "#1e293b",
                border: "1px solid #334155",
                borderRadius: 10,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              {pw.title || `${pw.category} Mechanism`}
            </button>
          ))}
        </div>
        {errorFeedback && (
          <div
            style={{
              marginTop: 20,
              padding: 15,
              background: "rgba(239,68,68,0.1)",
              border: "1px solid #ef4444",
              borderRadius: 10,
              color: "#fca5a5",
              fontSize: 14,
            }}
          >
            <strong>{errorFeedback.msg}</strong>
            <br />
            {errorFeedback.example}
            <br />
            <em>{errorFeedback.hint}</em>
          </div>
        )}
      </div>
    );
  }

  const steps = parsePairedSteps(pathway.steps);

  return (
    <div>
      <h3 style={{ color: "#38bdf8", marginBottom: 20 }}>
        {subMode === "test"
          ? `Building Pathway for: ${currentDrug.displayName}`
          : pathway.title || `${pathway.category} Pathway`}
      </h3>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 30,
        }}
      >
        {steps.map((step, i) => {
          // Static label — branch divider, no slot, no scoring
          if (step.type === "label") {
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: "6px 0 2px",
                }}
              >
                <div style={{ width: 36 }} />
                <div
                  style={{
                    flex: 1,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: "#38bdf8",
                    borderTop: "1px solid #1e3a4a",
                    paddingTop: 8,
                  }}
                >
                  {step.text}
                </div>
              </div>
            );
          }

          const slot = slots[i];
          const correct = checked && isSlotCorrect(step, slot);

          if (step.type === "paired") {
            const s = slot as {
              mol: string | null;
              dir: "↑" | "↓" | null;
            } | null;
            const chosenDir = s?.dir ?? null;
            const chosenMol = s?.mol ?? null;
            const borderColor = checked
              ? correct
                ? "#16a34a"
                : "#ef4444"
              : chosenMol
              ? "#38bdf8"
              : "#334155";

            return (
              <div
                key={i}
                style={{ display: "flex", alignItems: "stretch", gap: 8 }}
              >
                {/* Direction picker — left side */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    justifyContent: "center",
                  }}
                >
                  {(["↑", "↓"] as const).map((d) => {
                    const active = chosenDir === d;
                    const btnCorrect = checked && step.direction === d;
                    const btnWrong =
                      checked && chosenDir === d && step.direction !== d;
                    return (
                      <button
                        key={d}
                        onClick={() => handleDirToggle(i, d)}
                        disabled={checked}
                        style={{
                          width: 36,
                          height: 28,
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 700,
                          border: "1.5px solid",
                          borderColor: btnCorrect
                            ? "#16a34a"
                            : btnWrong
                            ? "#ef4444"
                            : active
                            ? "#38bdf8"
                            : "#334155",
                          background: btnCorrect
                            ? "rgba(34,197,94,0.15)"
                            : btnWrong
                            ? "rgba(239,68,68,0.15)"
                            : active
                            ? "rgba(56,189,248,0.15)"
                            : "#0f172a",
                          color: btnCorrect
                            ? "#4ade80"
                            : btnWrong
                            ? "#fca5a5"
                            : active
                            ? "#38bdf8"
                            : "#475569",
                          cursor: checked ? "default" : "pointer",
                          transition: "all 0.15s",
                          lineHeight: 1,
                        }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>

                {/* Molecule slot */}
                <div
                  onClick={() => handleSlotClick(i)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: 8,
                    border: `2px dashed ${borderColor}`,
                    background: chosenMol ? "#1e3a4a" : "transparent",
                    color: checked ? (correct ? "#4ade80" : "#fca5a5") : "#fff",
                    cursor: "pointer",
                    minHeight: 44,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {chosenMol ? (
                    <span>{chosenMol}</span>
                  ) : (
                    <span style={{ color: "#475569", fontSize: 12 }}>
                      [Tap to fill]
                    </span>
                  )}
                  {checked && !correct && (
                    <div
                      style={{ fontSize: 10, marginTop: 4, color: "#94a3b8" }}
                    >
                      Correct: {step.direction} {step.text}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Plain molecule slot — spacer keeps alignment with paired rows
          return (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <div style={{ width: 36 }} />
              <div
                onClick={() => handleSlotClick(i)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 8,
                  border: `2px dashed ${
                    checked
                      ? correct
                        ? "#16a34a"
                        : "#ef4444"
                      : slot
                      ? "#38bdf8"
                      : "#334155"
                  }`,
                  background: slot ? "#1e3a4a" : "transparent",
                  color: checked ? (correct ? "#4ade80" : "#fca5a5") : "#fff",
                  cursor: "pointer",
                  minHeight: 44,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {slot ? (
                  <span>{slot as string}</span>
                ) : (
                  <span style={{ color: "#475569", fontSize: 12 }}>
                    [Tap to fill]
                  </span>
                )}
                {checked && !correct && (
                  <div style={{ fontSize: 10, marginTop: 4, color: "#94a3b8" }}>
                    Correct: {step.text}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!checked ? (
        <div
          style={{
            background: "#1e293b",
            padding: 15,
            borderRadius: 12,
            position: "sticky",
            bottom: 20,
          }}
        >
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 10 }}>
            WORD BANK (includes distractors)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {bank.map((t, i) => (
              <button
                key={i}
                onClick={() => setSelected(t === selected ? null : t)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  background: selected === t ? "#38bdf8" : "#0f172a",
                  color: selected === t ? "#0f172a" : "#cbd5e1",
                  border: "1px solid #334155",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={checkBuild}
            style={{
              width: "100%",
              marginTop: 15,
              padding: 12,
              borderRadius: 8,
              background: "#38bdf8",
              color: "#0f172a",
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Check Pathway
          </button>
        </div>
      ) : subMode === "test" ? (
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={startTestMode}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 8,
              background: "#38bdf8",
              color: "#0f172a",
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Next
          </button>
          <button
            onClick={() => setPhase("select")}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 8,
              background: "#1e293b",
              color: "#38bdf8",
              border: "1px solid #38bdf8",
              cursor: "pointer",
            }}
          >
            Finish & Return
          </button>
        </div>
      ) : (
        <button
          onClick={() => setPhase("select")}
          style={{
            width: "100%",
            padding: 12,
            borderRadius: 8,
            background: "#1e293b",
            color: "#38bdf8",
            border: "1px solid #38bdf8",
            cursor: "pointer",
          }}
        >
          Finish & Return
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Hemodynamics Mode
// ══════════════════════════════════════════════════════════════════════
function HemodynamicsMode() {
  const [tab, setTab] = useState("p1");

  const tabs = [
    { key: "p1", label: "1. Perfusion", color: "#ef4444" },
    { key: "p2", label: "2. Tank", color: "#38bdf8" },
    { key: "p3", label: "3. Pipes", color: "#818cf8" },
    { key: "p4", label: "4. Pump", color: "#4ade80" },
    { key: "safety", label: "Safety", color: "#fbbf24" },
  ];

  const pillStyle = (t: any) => ({
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: tab === t.key ? 600 : 400,
    cursor: "pointer",
    border: `1.5px solid ${tab === t.key ? t.color : "#334155"}`,
    background: tab === t.key ? `${t.color}18` : "transparent",
    color: tab === t.key ? t.color : "#94a3b8",
    transition: "all 0.2s",
  });

  const boxStyle = (color: string) => ({
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    border: `1px solid ${color}30`,
    background: `${color}08`,
  });

  const branchContainer: React.CSSProperties = {
    display: "flex",
    gap: 10,
    marginTop: 10,
  };

  const branchItem = (type: string): React.CSSProperties => ({
    flex: 1,
    borderRadius: 10,
    padding: 12,
    fontSize: 12,
    lineHeight: 1.5,
    background:
      type === "yes" ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)",
    border: `1px solid ${
      type === "yes" ? "rgba(239,68,68,0.2)" : "rgba(34,197,94,0.2)"
    }`,
    color: "#cbd5e1",
  });

  const branchTag = (type: string): React.CSSProperties => ({
    fontWeight: 600,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
    color: type === "yes" ? "#fca5a5" : "#86efac",
  });

  const drugPill: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    margin: "2px 2px 2px 0",
    background: "rgba(129,140,248,0.15)",
    color: "#a5b4fc",
  };

  const fluidPill: React.CSSProperties = {
    ...drugPill,
    background: "rgba(56,189,248,0.15)",
    color: "#7dd3fc",
  };
  const alertPill: React.CSSProperties = {
    ...drugPill,
    background: "rgba(251,191,36,0.15)",
    color: "#fcd34d",
  };

  const qLabel: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: "#e2e8f0",
    marginBottom: 8,
  };
  const subLabel: React.CSSProperties = {
    fontSize: 12,
    color: "#cbd5e1",
    marginBottom: 6,
  };
  const noteStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#94a3b8",
    lineHeight: 1.5,
    marginTop: 6,
  };
  const arrowDown: React.CSSProperties = {
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 18,
    margin: "4px 0",
  };

  const safetyCard = (color: string): React.CSSProperties => ({
    borderRadius: 10,
    padding: 12,
    fontSize: 12,
    lineHeight: 1.5,
    color: "#cbd5e1",
    background: `${color}10`,
    border: `1px solid ${color}25`,
  });

  const safetyTitle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 13,
    color: "#e2e8f0",
    marginBottom: 6,
  };

  return (
    <div>
      <div
        style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={pillStyle(t)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "p1" && (
        <div>
          <div style={boxStyle("#ef4444")}>
            <div style={qLabel}>Is MAP &lt; 60 mmHg?</div>
            <div style={branchContainer}>
              <div style={branchItem("yes")}>
                <div style={branchTag("yes")}>Yes — critical</div>
                Immediate intervention required. Assess volume status first
                (step 2), then vascular resistance (step 3).
              </div>
              <div style={branchItem("no")}>
                <div style={branchTag("no")}>No</div>
                Perfusion adequate for now. Proceed to step 2 to optimize
                hemodynamic status.
              </div>
            </div>
          </div>
          <div style={noteStyle}>
            MAP = mean arterial pressure. Target ≥ 65 mmHg in most clinical
            contexts. MAP &lt; 60 indicates organ hypoperfusion risk.
          </div>
        </div>
      )}

      {tab === "p2" && (
        <div>
          <div style={boxStyle("#38bdf8")}>
            <div style={qLabel}>Assess preload: CVP &amp; PCWP</div>
            <div style={branchContainer}>
              <div style={branchItem("yes")}>
                <div style={branchTag("yes")}>
                  Low volume — CVP &lt; −1 or PCWP &lt; 4
                </div>
                <span style={fluidPill}>Crystalloids</span>{" "}
                <span style={fluidPill}>Colloids</span>
                <br />
                Normal saline, Ringer's lactate, albumin, or pentaspan.
                <br />
                Check Hgb: if &lt; 120 g/L → <span style={drugPill}>pRBCs</span>
              </div>
              <div style={branchItem("no")}>
                <div style={branchTag("no")}>
                  Fluid overload — PCWP &gt; 18 or CVP &gt; 8
                </div>
                <span style={drugPill}>Furosemide (Lasix)</span>
                <br />
                Reduce circulating volume. Monitor UO response.
              </div>
            </div>
          </div>
          <div style={arrowDown}>↓</div>
          <div style={boxStyle("#38bdf8")}>
            <div style={qLabel}>Check albumin</div>
            <div style={subLabel}>
              If albumin &lt; 33 g/L → insufficient oncotic pressure to retain
              intravascular fluid
            </div>
            <span style={fluidPill}>Albumin replacement</span>
          </div>
          <div style={arrowDown}>↓</div>
          <div style={boxStyle("#38bdf8")}>
            <div style={qLabel}>Check urine output</div>
            <div style={subLabel}>
              UO &lt; 30 mL/hr → reflects poor renal perfusion or end-organ
              shock
            </div>
            <div style={noteStyle}>
              Low UO is an indicator, not a direct treatment target — address
              underlying cause (volume, pressure, or pump).
            </div>
          </div>
        </div>
      )}

      {tab === "p3" && (
        <div>
          <div style={boxStyle("#818cf8")}>
            <div style={qLabel}>Assess SVR (systemic vascular resistance)</div>
            <div style={branchContainer}>
              <div style={branchItem("yes")}>
                <div style={branchTag("yes")}>
                  Low SVR — &lt; 900 dyne·sec·cm⁻⁵
                </div>
                Vasodilation / distributive shock
                <br />
                <span style={drugPill}>Norepinephrine (Levophed)</span>{" "}
                <span style={drugPill}>Phenylephrine</span>
              </div>
              <div style={branchItem("no")}>
                <div style={branchTag("no")}>
                  High SVR — &gt; 1400 dyne·sec·cm⁻⁵
                </div>
                Excessive vasoconstriction → increased afterload
                <br />
                <span style={drugPill}>Nitroprusside</span>{" "}
                <span style={drugPill}>Nitroglycerin</span>
              </div>
            </div>
          </div>
          <div style={noteStyle}>
            Normal SVR: 900–1400 dyne·sec·cm⁻⁵. α₁ agonists increase SVR; direct
            vasodilators (NO donors) decrease it.
          </div>
        </div>
      )}

      {tab === "p4" && (
        <div>
          <div style={boxStyle("#4ade80")}>
            <div style={qLabel}>Is cardiac output (CO) &lt; 4 L/min?</div>
            <div style={branchContainer}>
              <div style={branchItem("yes")}>
                <div style={branchTag("yes")}>Yes — low CO</div>
                <span style={drugPill}>Dobutamine</span> (β₁ agonist — first
                line)
                <br />
                <span style={drugPill}>Milrinone</span> (PDE3 inhibitor — if
                β-blocked)
                <br />
                <div style={{ ...noteStyle, marginTop: 6, color: "#fbbf24" }}>
                  ⚠ If patient is on a β-blocker, dobutamine will be ineffective
                  → use milrinone
                </div>
              </div>
              <div style={branchItem("no")}>
                <div style={branchTag("no")}>CO adequate</div>
                Monitor. If CO elevated + low SVR → consider septic/distributive
                picture.
              </div>
            </div>
          </div>
          <div style={arrowDown}>↓</div>
          <div style={boxStyle("#4ade80")}>
            <div style={qLabel}>Check heart rate</div>
            <div style={subLabel}>
              HR &gt; 100 bpm → increased myocardial O₂ demand
            </div>
            <div style={noteStyle}>
              Avoid chronotropes. Tachycardia often reflects compensation —
              treat the underlying cause (hypovolemia, pain, fever) rather than
              the rate itself.
            </div>
          </div>
        </div>
      )}

      {tab === "safety" && (
        <div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <div style={safetyCard("#818cf8")}>
              <div style={safetyTitle}>β-blocker conflict</div>
              Patient on metoprolol, labetalol, etc.?
              <br />
              <br />
              β₁ agonists (<span style={drugPill}>dobutamine</span>{" "}
              <span style={drugPill}>salbutamol</span>) will be{" "}
              <strong style={{ color: "#fca5a5" }}>blocked</strong>.<br />
              <br />
              <strong style={{ color: "#e2e8f0" }}>Solution:</strong> Use{" "}
              <span style={drugPill}>milrinone</span> for CO — PDE3 inhibitor
              bypasses the β-receptor entirely.
            </div>
            <div style={safetyCard("#fbbf24")}>
              <div style={safetyTitle}>K⁺ (normal 3.5–5.0 mmol/L)</div>
              <strong style={{ color: "#e2e8f0" }}>Low K⁺:</strong> ↑ risk of{" "}
              <span style={alertPill}>digoxin toxicity</span> — hypokalemia
              sensitizes Na⁺/K⁺-ATPase binding site.
              <br />
              <br />
              <strong style={{ color: "#e2e8f0" }}>High K⁺:</strong> ↑ risk of{" "}
              <span style={alertPill}>cardiac arrest with succinylcholine</span>{" "}
              — depolarization releases more K⁺ from muscle cells.
            </div>
            <div style={safetyCard("#38bdf8")}>
              <div style={safetyTitle}>Na⁺ (normal 136–145 mmol/L)</div>
              Hypernatremia suggests dehydration or excess NS infusion.
              <br />
              <br />
              Consider switching from normal saline (154 mmol/L Na⁺) to a
              balanced crystalloid (e.g., Ringer's lactate).
            </div>
            <div style={safetyCard("#818cf8")}>
              <div style={safetyTitle}>Inotrope at pump step</div>
              Before starting dobutamine,{" "}
              <strong style={{ color: "#e2e8f0" }}>always check</strong> current
              β-blocker status.
              <br />
              <br />
              This is the most common missed drug interaction in hemodynamic
              management.
            </div>
          </div>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12,
              marginTop: 16,
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <thead>
              <tr style={{ background: "#1e293b" }}>
                {["Parameter", "If low", "If high"].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: 10,
                      textAlign: "left",
                      color: "#e2e8f0",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                [
                  "MAP",
                  "Critical perfusion risk",
                  "Hypertension / high workload",
                ],
                ["CVP / PCWP", "Needs volume / blood", "Needs diuretics"],
                ["SVR", "Needs vasopressors", "Needs vasodilators"],
                ["CO", "Needs inotropes", "High demand / sepsis"],
              ].map(([param, low, high], i) => (
                <tr key={i} style={{ borderTop: "1px solid #1e293b" }}>
                  <td
                    style={{ padding: 10, color: "#cbd5e1", fontWeight: 500 }}
                  >
                    {param}
                  </td>
                  <td style={{ padding: 10, color: "#cbd5e1" }}>{low}</td>
                  <td style={{ padding: 10, color: "#cbd5e1" }}>{high}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Name Match Game
// ══════════════════════════════════════════════════════════════════════
function NameMatchGame({
  drugs,
  includeNoTrade = false,
}: {
  drugs: DrugEntry[];
  // When true, drugs without a trade name are kept and shown as "N/A"
  includeNoTrade?: boolean;
}) {
  const ROUND_SIZE = 8;

  const hasTrade = (d: DrugEntry) => !!d.trade && d.trade !== "N/A";
  const tradeOf = (d: DrugEntry) => (hasTrade(d) ? d.trade! : "N/A");

  const pool = useMemo(
    () =>
      drugs.filter(
        (d) => d._indicationIndex === 0 && (includeNoTrade || hasTrade(d))
      ),
    [drugs, includeNoTrade]
  );

  type ColKey = "trade" | "generic" | "classLabel";

  interface RoundItem {
    id: string;
    trade: string;
    generic: string;
    classLabel: string;
  }

  const [round, setRound] = useState<RoundItem[]>([]);
  const [cols, setCols] = useState<Record<ColKey, string[]>>({
    trade: [],
    generic: [],
    classLabel: [],
  });
  // Selections and solved buttons are tracked by button index so duplicate
  // values (e.g. several "LABA + ICS") behave as separate buttons.
  const [selections, setSelections] = useState<Partial<Record<ColKey, number>>>(
    {}
  );
  const [solvedSlots, setSolvedSlots] = useState<Record<ColKey, number[]>>({
    trade: [],
    generic: [],
    classLabel: [],
  });
  const [solvedIds, setSolvedIds] = useState<string[]>([]);
  const [flash, setFlash] = useState<"correct" | "wrong" | null>(null);
  const [score, setScore] = useState({ correct: 0, attempts: 0 });

  // Mistake tracking across rounds
  const [mistakeIds, setMistakeIds] = useState<Set<string>>(new Set());
  const [correctStreaks, setCorrectStreaks] = useState<Record<string, number>>(
    {}
  );
  const [graduatedIds, setGraduatedIds] = useState<Set<string>>(new Set());
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set()); // ← add here

  const buildRound = useCallback(
    (
      currentMistakeIds: Set<string>,
      currentGraduatedIds: Set<string>,
      currentSeenIds: Set<string>
    ): RoundItem[] => {
      const toRepeat = [...currentMistakeIds].filter(
        (id) => !currentGraduatedIds.has(id)
      );
      const repeatItems = toRepeat
        .map((id) => pool.find((d) => d.id === id))
        .filter(Boolean)
        .map((d) => ({
          id: d!.id,
          trade: tradeOf(d!),
          generic: d!.generic,
          classLabel: d!.classLabel,
        })) as RoundItem[];

      const padCount = Math.max(0, ROUND_SIZE - repeatItems.length);
      const excludeIds = new Set([...toRepeat, ...currentGraduatedIds]);

      const unseen = pool.filter(
        (d) => !excludeIds.has(d.id) && !currentSeenIds.has(d.id)
      );
      const seen = pool.filter(
        (d) => !excludeIds.has(d.id) && currentSeenIds.has(d.id)
      );

      // Fill pad slots: 80% preference for unseen, 20% from seen;
      // if either group runs short, the other fills the remaining slots
      const preferredUnseen = Math.min(unseen.length, Math.ceil(padCount * 0.8));
      const seenCount = Math.min(seen.length, padCount - preferredUnseen);
      const actualUnseenCount = Math.min(unseen.length, padCount - seenCount);

      const padItems = [
        ...shuffle(unseen).slice(0, actualUnseenCount),
        ...shuffle(seen).slice(0, padCount - actualUnseenCount),
      ]
        .slice(0, padCount)
        .map((d) => ({
          id: d.id,
          trade: tradeOf(d),
          generic: d.generic,
          classLabel: d.classLabel,
        }));

      return shuffle([...repeatItems, ...padItems]);
    },
    [pool]
  );

  const applyRound = useCallback((items: RoundItem[]) => {
    setRound(items);
    setCols({
      trade: shuffle(items.map((i) => i.trade)),
      generic: shuffle(items.map((i) => i.generic)),
      classLabel: shuffle(items.map((i) => i.classLabel)),
    });
    setSelections({});
    setSolvedSlots({ trade: [], generic: [], classLabel: [] });
    setSolvedIds([]);
    setFlash(null);
  }, []);

  const startRound = useCallback(() => {
    setMistakeIds(new Set());
    setCorrectStreaks({});
    setGraduatedIds(new Set());
    setSeenIds(new Set());
    setScore({ correct: 0, attempts: 0 });
    const items = buildRound(new Set(), new Set(), new Set());
    applyRound(items);
  }, [buildRound, applyRound]);

  const nextRound = useCallback(
    (
      currentMistakeIds: Set<string>,
      currentGraduatedIds: Set<string>,
      currentSeenIds: Set<string>
    ) => {
      const items = buildRound(
        currentMistakeIds,
        currentGraduatedIds,
        currentSeenIds
      );
      // Every drug has graduated: start over instead of showing an empty round
      if (items.length === 0) {
        startRound();
        return;
      }
      applyRound(items);
    },
    [buildRound, applyRound, startRound]
  );

  useEffect(() => {
    if (pool.length >= ROUND_SIZE) startRound();
  }, [startRound, pool]);

  const handleSelect = (idx: number, col: ColKey) => {
    if (flash) return;
    if (solvedSlots[col].includes(idx)) return;

    const nextIdx = { ...selections, [col]: idx };
    setSelections(nextIdx);

    if (
      nextIdx.trade !== undefined &&
      nextIdx.generic !== undefined &&
      nextIdx.classLabel !== undefined
    ) {
      const next = {
        trade: cols.trade[nextIdx.trade],
        generic: cols.generic[nextIdx.generic],
        classLabel: cols.classLabel[nextIdx.classLabel],
      };
      const match = round.find(
        (item) =>
          !solvedIds.includes(item.id) &&
          item.trade === next.trade &&
          item.generic === next.generic &&
          item.classLabel === next.classLabel
      );

      setScore((s) => ({
        correct: s.correct + (match ? 1 : 0),
        attempts: s.attempts + 1,
      }));

      if (match) {
        // Increment streak
        setCorrectStreaks((prev) => {
          const newStreak = (prev[match.id] ?? 0) + 1;
          const updated = { ...prev, [match.id]: newStreak };

          if (newStreak >= 2) {
            setGraduatedIds((g) => new Set([...g, match.id]));
          }

          return updated;
        });
        setSeenIds((prev) => new Set([...prev, match.id]));

        setFlash("correct");
        setTimeout(() => {
          setSolvedIds((prev) => [...prev, match.id]);
          setSolvedSlots((prev) => ({
            trade: [...prev.trade, nextIdx.trade!],
            generic: [...prev.generic, nextIdx.generic!],
            classLabel: [...prev.classLabel, nextIdx.classLabel!],
          }));
          setSelections({});
          setFlash(null);
        }, 600);
      } else {
        // Wrong — find which drug(s) were part of the attempted triplet and penalize
        const wrongDrug = round.find(
          (item) =>
            item.trade === next.trade ||
            item.generic === next.generic ||
            item.classLabel === next.classLabel
        );
        if (wrongDrug) {
          setMistakeIds((prev) => new Set([...prev, wrongDrug.id]));
          // Reset streak for any involved drugs
          setCorrectStreaks((prev) => {
            const updated = { ...prev };
            round
              .filter(
                (item) =>
                  item.trade === next.trade ||
                  item.generic === next.generic ||
                  item.classLabel === next.classLabel
              )
              .forEach((item) => {
                updated[item.id] = 0;
              });
            return updated;
          });
        }

        setFlash("wrong");
        setTimeout(() => {
          setSelections({});
          setFlash(null);
        }, 600);
      }
    }
  };

  const allSolved = solvedIds.length === round.length && round.length > 0;

  const btnStyle = (col: ColKey, idx: number): React.CSSProperties => {
    const isSolved = solvedSlots[col].includes(idx);
    const isSelected = selections[col] === idx;

    let bg = "#1e293b";
    let border = "#334155";
    let color = "#e2e8f0";
    let opacity: number = 1;

    if (isSolved) {
      bg = "rgba(34,197,94,0.12)";
      border = "#22c55e";
      color = "#4ade80";
      opacity = 0.55;
    } else if (isSelected && flash === "correct") {
      bg = "rgba(34,197,94,0.18)";
      border = "#22c55e";
      color = "#4ade80";
    } else if (isSelected && flash === "wrong") {
      bg = "rgba(239,68,68,0.15)";
      border = "#ef4444";
      color = "#fca5a5";
    } else if (isSelected) {
      bg = "rgba(56,189,248,0.15)";
      border = "#38bdf8";
      color = "#7dd3fc";
    }

    return {
      width: "100%",
      padding: "10px 8px",
      borderRadius: 8,
      background: bg,
      border: `1.5px solid ${border}`,
      color,
      opacity,
      cursor: isSolved ? "default" : "pointer",
      fontSize: 12,
      fontWeight: 500,
      textAlign: "center",
      transition: "all 0.2s",
      minHeight: 44,
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.3,
    };
  };

  if (pool.length < ROUND_SIZE)
    return (
      <p style={{ color: "#64748b", textAlign: "center" }}>
        Need at least {ROUND_SIZE} drugs with trade names selected.
      </p>
    );

  const colHeaders: { key: ColKey; label: string }[] = [
    { key: "trade", label: "Trade Name" },
    { key: "generic", label: "Generic Name" },
    { key: "classLabel", label: "Drug Class" },
  ];

  const repeatCount = [...mistakeIds].filter((id) => !graduatedIds.has(id)).length;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 14, color: "#cbd5e1" }}>
            NameDrill
          </h3>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
            Select one from each column to form a matching triplet.
            {repeatCount > 0 && (
              <span style={{ color: "#fb923c", marginLeft: 8 }}>
                {repeatCount} drug{repeatCount > 1 ? "s" : ""} repeating until
                mastered.
              </span>
            )}
          </div>
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
          {score.correct}/{score.attempts} correct
        </span>
      </div>

      {allSolved ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "#93c5fd",
              marginBottom: 8,
            }}
          >
            Round Complete
          </div>
          {repeatCount > 0 && (
            <p style={{ color: "#fb923c", fontSize: 13, marginBottom: 8 }}>
              {repeatCount} drug{repeatCount > 1 ? "s" : ""} still need 2
              correct in a row.
            </p>
          )}
          <p style={{ color: "#94a3b8", marginBottom: 20 }}>
            {score.correct}/{score.attempts} correct attempts
          </p>
          <button
            onClick={() => nextRound(mistakeIds, graduatedIds, seenIds)}
            style={{
              padding: "10px 28px",
              borderRadius: 10,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {repeatCount > 0 ? "Next Round (mistakes carry over)" : "New Round"}
          </button>
          {repeatCount > 0 && (
            <button
              onClick={startRound}
              style={{
                marginLeft: 12,
                padding: "10px 28px",
                borderRadius: 10,
                background: "transparent",
                color: "#94a3b8",
                border: "1px solid #334155",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Reset
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
          }}
        >
          {colHeaders.map(({ key, label }) => (
            <div key={key}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#64748b",
                  textAlign: "center",
                  marginBottom: 8,
                }}
              >
                {label}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cols[key].map((val, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelect(idx, key)}
                    style={btnStyle(key, idx)}
                    title={val}
                  >
                    {val}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════════
// ── Inhaler Devices & Doses
// ══════════════════════════════════════════════════════════════════════
// Data: pharmdrill_inhalers.json (one entry per product; devices[] holds the
// per-device dose and dosage order). Devices flagged `optional` (asterisked
// in the course list) are not graded either way.
interface InhalerDevice {
  name: string;
  dose: string;
  order: string;
  note?: string;
  optional?: boolean;
  // Shown in brackets after the device name in the reveal panel
  revealDose?: string;
}

interface Inhaler {
  id: string;
  generic: string;
  trade: string;
  classLabel: string;
  timing: string;
  devices: InhalerDevice[];
}

interface McPart {
  label: string;
  correct: string;
  options: string[];
}

const INHALERS: Inhaler[] = inhalerData as Inhaler[];
const INHALER_DEVICE_NAMES = [
  ...new Set(INHALERS.flatMap((i) => i.devices.map((d) => d.name))),
].sort();
// Padding so the Duration question always has 5 options
const EXTRA_DURATIONS = ["1–2 h", "6–8 h", "48 h"];

const pickInhalerName = (inh: Inhaler) =>
  Math.random() < 0.5 ? inh.trade : inh.generic;

// Loose comparison key so near-identical text never shows up as a wrong option
const answerKey = (s: string) =>
  s
    .split("|")[0]
    .replace(/\(.*?\)/g, "")
    .replace(/[~\s]/g, "")
    .toLowerCase();

const getDuration = (timing: string) => {
  const m = timing.match(/Duration:\s*([^;(]+)/);
  return m ? m[1].trim() : "";
};

const buildMcPart = (
  label: string,
  correct: string,
  preferred: string[],
  rest: string[]
): McPart => {
  const seen = new Set([answerKey(correct)]);
  const picks: string[] = [];
  for (const s of [...shuffle(preferred), ...shuffle(rest)]) {
    if (picks.length === 4) break;
    if (!s || seen.has(answerKey(s))) continue;
    seen.add(answerKey(s));
    picks.push(s);
  }
  return { label, correct, options: shuffle([correct, ...picks]) };
};

const buildDeviceDeck = () =>
  shuffle(INHALERS).map((inh) => ({ inh, name: pickInhalerName(inh) }));

const buildDoseDeck = () => {
  const pairs = INHALERS.flatMap((inh) =>
    inh.devices.map((dev) => ({ inh, dev }))
  );
  const durations = [
    ...INHALERS.map((i) => getDuration(i.timing)),
    ...EXTRA_DURATIONS,
  ];
  return shuffle(
    pairs.flatMap((pair) => {
      const { inh, dev } = pair;
      // Same-device distractors first (harder), then everything else
      const sameDev = (f: (d: InhalerDevice) => string) =>
        pairs
          .filter((p) => p !== pair && p.dev.name === dev.name)
          .map((p) => f(p.dev));
      const otherDev = (f: (d: InhalerDevice) => string) =>
        pairs.filter((p) => p.dev.name !== dev.name).map((p) => f(p.dev));
      const parts: McPart[] = [];
      if (dev.dose)
        parts.push(
          buildMcPart(
            "Dose per inhalation",
            dev.dose,
            sameDev((d) => d.dose),
            otherDev((d) => d.dose)
          )
        );
      if (dev.order)
        parts.push(
          buildMcPart(
            "Typical dosage order",
            dev.order,
            sameDev((d) => d.order),
            otherDev((d) => d.order)
          )
        );
      const dur = getDuration(inh.timing);
      if (dur) parts.push(buildMcPart("Duration", dur, [], durations));
      return parts.length
        ? [{ inh, dev, name: pickInhalerName(inh), parts }]
        : [];
    })
  );
};

// Decks loop endlessly: past the last question, reshuffle and keep going
// (score carries over). Rotate so the new pass doesn't open on the same drug.
const reshuffleAfter = <T extends { inh: Inhaler }>(
  build: () => T[],
  last: T
): T[] => {
  const d = build();
  return d.length > 1 && d[0].inh.id === last.inh.id
    ? [...d.slice(1), d[0]]
    : d;
};

const inhalerBtn = (bg: string): React.CSSProperties => ({
  padding: "10px 32px",
  borderRadius: 10,
  background: bg,
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
});

function InhalerReveal({ inh }: { inh: Inhaler }) {
  const notes = inh.devices.filter((d) => d.note);
  return (
    <div
      style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 10,
        background: "#0f172a",
        border: "1px solid #334155",
        fontSize: 13,
        color: "#cbd5e1",
        lineHeight: 1.6,
      }}
    >
      <div style={{ fontWeight: 700, color: "#e2e8f0" }}>
        {inh.trade} · {inh.generic}{" "}
        <span style={{ color: "#94a3b8", fontWeight: 400 }}>
          ({inh.classLabel})
        </span>
      </div>
      <div>
        Devices:{" "}
        {inh.devices
          .map(
            (d) =>
              (d.optional ? `${d.name}*` : d.name) +
              (d.revealDose ? ` (${d.revealDose})` : "")
          )
          .join(", ")}
      </div>
      {inh.timing && <div>{inh.timing}</div>}
      {notes.map((d) => (
        <div key={d.name} style={{ color: "#94a3b8" }}>
          {d.note}
        </div>
      ))}
    </div>
  );
}

function InhalerScore({
  idx,
  total,
  correct,
  answered,
}: {
  idx: number;
  total: number;
  correct: number;
  answered: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 16,
        fontSize: 12,
        color: "#94a3b8",
      }}
    >
      <span>
        Question {Math.min(idx + 1, total)} / {total}
      </span>
      <span>
        {correct}/{answered} correct (
        {answered > 0 ? Math.round((correct / answered) * 100) : 0}%)
      </span>
    </div>
  );
}

function InhalerDeviceQuiz() {
  const [deck, setDeck] = useState(buildDeviceDeck);
  const [idx, setIdx] = useState(0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [stats, setStats] = useState({ answered: 0, correct: 0 });


  const { inh, name } = deck[idx];
  const required = new Set(
    inh.devices.filter((d) => !d.optional).map((d) => d.name)
  );
  const optional = new Set(
    inh.devices.filter((d) => d.optional).map((d) => d.name)
  );

  const toggle = (dev: string) => {
    if (submitted) return;
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(dev) ? next.delete(dev) : next.add(dev);
      return next;
    });
  };

  const submit = () => {
    const ok = INHALER_DEVICE_NAMES.every(
      (dev) => optional.has(dev) || required.has(dev) === checked.has(dev)
    );
    setSubmitted(true);
    setStats((s) => ({
      answered: s.answered + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
  };

  const next = () => {
    if (idx + 1 >= deck.length) {
      setDeck(reshuffleAfter(buildDeviceDeck, deck[idx]));
      setIdx(0);
    } else setIdx(idx + 1);
    setChecked(new Set());
    setSubmitted(false);
  };

  return (
    <div>
      <InhalerScore
        idx={idx}
        total={deck.length}
        correct={stats.correct}
        answered={stats.answered}
      />
      <div
        style={{
          padding: 20,
          borderRadius: 14,
          background: "#1e293b",
          border: "1px solid #334155",
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: 15, color: "#e2e8f0", lineHeight: 1.6 }}>
          Which device(s) does <strong>{name}</strong> come in?
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
          Select all that apply, then submit.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 8,
        }}
      >
        {INHALER_DEVICE_NAMES.map((dev) => {
          const isChecked = checked.has(dev);
          const isRequired = required.has(dev);
          let bg = isChecked ? "rgba(37,99,235,0.15)" : "#1e293b";
          let borderColor = isChecked ? "#2563eb" : "#334155";
          let textColor = "#e2e8f0";
          let tag = "";
          if (submitted) {
            if (optional.has(dev)) {
              borderColor = "#64748b";
              textColor = "#94a3b8";
              tag = " (optional*)";
            } else if (isRequired && isChecked) {
              bg = "rgba(34,197,94,0.12)";
              borderColor = "#16a34a";
              textColor = "#4ade80";
              tag = " ✓";
            } else if (isRequired) {
              bg = "rgba(234,179,8,0.10)";
              borderColor = "#eab308";
              textColor = "#fde047";
              tag = " (missed)";
            } else if (isChecked) {
              bg = "rgba(239,68,68,0.12)";
              borderColor = "#ef4444";
              textColor = "#fca5a5";
              tag = " ✕";
            } else {
              borderColor = "#1e293b";
              textColor = "#475569";
            }
          }
          return (
            <label
              key={dev}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                borderRadius: 10,
                background: bg,
                border: `1.5px solid ${borderColor}`,
                color: textColor,
                cursor: submitted ? "default" : "pointer",
                fontSize: 14,
                transition: "all 0.2s",
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={submitted}
                onChange={() => toggle(dev)}
              />
              {dev}
              {tag}
            </label>
          );
        })}
      </div>

      {submitted && <InhalerReveal inh={inh} />}

      <div style={{ marginTop: 16, textAlign: "center" }}>
        {submitted ? (
          <button onClick={next} style={inhalerBtn("#2563eb")}>
            Next Question →
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={checked.size === 0}
            style={{
              ...inhalerBtn(checked.size === 0 ? "#334155" : "#16a34a"),
              cursor: checked.size === 0 ? "default" : "pointer",
            }}
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
}

function InhalerDoseQuiz() {
  const [deck, setDeck] = useState(buildDoseDeck);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [stats, setStats] = useState({ answered: 0, correct: 0 });


  const { inh, dev, name, parts } = deck[idx];
  const allAnswered = parts.every((_, i) => answers[i] !== undefined);

  // Each part counts as its own scored answer
  const submit = () => {
    const right = parts.filter((p, i) => answers[i] === p.correct).length;
    setSubmitted(true);
    setStats((s) => ({
      answered: s.answered + parts.length,
      correct: s.correct + right,
    }));
  };

  const next = () => {
    if (idx + 1 >= deck.length) {
      setDeck(reshuffleAfter(buildDoseDeck, deck[idx]));
      setIdx(0);
    } else setIdx(idx + 1);
    setAnswers({});
    setSubmitted(false);
  };

  return (
    <div>
      <InhalerScore
        idx={idx}
        total={deck.length}
        correct={stats.correct}
        answered={stats.answered}
      />
      <div
        style={{
          padding: 20,
          borderRadius: 14,
          background: "#1e293b",
          border: "1px solid #334155",
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: 15, color: "#e2e8f0", lineHeight: 1.6 }}>
          <strong>{name}</strong> via{" "}
          <strong>
            {dev.name}
            {dev.optional ? "*" : ""}
          </strong>
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
          Answer each part, then submit.
        </p>
      </div>

      {parts.map((part, pi) => (
        <div key={pi} style={{ marginBottom: 18 }}>
          <h4
            style={{
              margin: "0 0 8px",
              fontSize: 13,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {part.label}
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {part.options.map((opt, oi) => {
              const isSelected = answers[pi] === opt;
              const isCorrect = opt === part.correct;
              let bg = isSelected ? "rgba(37,99,235,0.15)" : "#1e293b";
              let borderColor = isSelected ? "#2563eb" : "#334155";
              let textColor = "#e2e8f0";
              if (submitted) {
                if (isCorrect) {
                  bg = "rgba(34,197,94,0.12)";
                  borderColor = "#16a34a";
                  textColor = "#4ade80";
                } else if (isSelected) {
                  bg = "rgba(239,68,68,0.12)";
                  borderColor = "#ef4444";
                  textColor = "#fca5a5";
                } else {
                  bg = "#1e293b";
                  borderColor = "#1e293b";
                  textColor = "#475569";
                }
              }
              return (
                <button
                  key={oi}
                  onClick={() =>
                    !submitted && setAnswers((a) => ({ ...a, [pi]: opt }))
                  }
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "12px 16px",
                    borderRadius: 10,
                    textAlign: "left",
                    background: bg,
                    border: `1.5px solid ${borderColor}`,
                    color: textColor,
                    cursor: submitted ? "default" : "pointer",
                    transition: "all 0.2s",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600, color: submitted ? textColor : "#64748b" }}>
                    {String.fromCharCode(65 + oi)}.
                  </span>
                  <span style={{ flex: 1 }}>{renderPipeField(opt)}</span>
                  {submitted && isCorrect && <span>✓</span>}
                  {submitted && isSelected && !isCorrect && <span>✕</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {submitted && <InhalerReveal inh={inh} />}

      <div style={{ marginTop: 16, textAlign: "center" }}>
        {submitted ? (
          <button onClick={next} style={inhalerBtn("#2563eb")}>
            Next Question →
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={!allAnswered}
            style={{
              ...inhalerBtn(allAnswered ? "#16a34a" : "#334155"),
              cursor: allAnswered ? "pointer" : "default",
            }}
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
}

function InhalerDeviceMode() {
  const [tab, setTab] = useState<"devices" | "doses">("devices");
  const tabs: { key: "devices" | "doses"; label: string }[] = [
    { key: "devices", label: "🧰 Devices (select all)" },
    { key: "doses", label: "💊 Doses (multiple choice)" },
  ];
  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1,
              padding: "10px 12px",
              borderRadius: 10,
              border: `1.5px solid ${tab === t.key ? "#38bdf8" : "#334155"}`,
              background: tab === t.key ? "rgba(56,189,248,0.12)" : "#1e293b",
              color: tab === t.key ? "#38bdf8" : "#e2e8f0",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "devices" ? <InhalerDeviceQuiz /> : <InhalerDoseQuiz />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ── Quiz Mode
// ══════════════════════════════════════════════════════════════════════
function QuizMode({
  drugs,
  showCascade,
}: {
  drugs: DrugEntry[];
  showCascade: boolean;
}) {
  const QUESTION_TYPES = [
    "whatMOA",
    "whichDrug",
    "tradeToGeneric",
    "classIdentify",
  ];

  const generateQuestion = useCallback(() => {
    if (drugs.length < 4) return null;

    const hasTradeNames = drugs.some((d) => d.trade && d.trade !== "N/A");
    const validTypes = hasTradeNames
      ? QUESTION_TYPES
      : QUESTION_TYPES.filter((t) => t !== "tradeToGeneric");

    const type = validTypes[Math.floor(Math.random() * validTypes.length)];

    let correct: DrugEntry, distractors: DrugEntry[], allOpts: DrugEntry[];

    if (type === "tradeToGeneric") {
      const tradeDrugs = drugs.filter((d) => d.trade && d.trade !== "N/A");
      correct = tradeDrugs[Math.floor(Math.random() * tradeDrugs.length)];
      distractors = shuffle(drugs.filter((d) => d.id !== correct.id)).slice(
        0,
        3
      );
    } else {
      const shuffled = shuffle(drugs);
      correct = shuffled[0];
      distractors = shuffled.slice(1, 4);
    }

    allOpts = shuffle([correct, ...distractors]);

    let question: string,
      options: { id: string; text: string }[],
      correctId: string;

    const formatName = (d: DrugEntry) =>
      d.trade && d.trade !== "N/A" ? `${d.generic} (${d.trade})` : d.generic;

    switch (type) {
      case "whatMOA": {
        question = `What is the mode of action of ${formatName(correct)}?`;
        options = allOpts.map((d) => ({
          id: d.id,
          text: showCascade ? d.cascade : `${d.classLabel}: ${d.primaryAction}`,
        }));
        correctId = correct.id;
        break;
      }
      case "whichDrug": {
        const moaText = showCascade
          ? correct.cascade
          : `${correct.classLabel}: ${correct.primaryAction}`;
        question = `Which drug has this mechanism?\n"${moaText}"`;
        options = allOpts.map((d) => ({ id: d.id, text: formatName(d) }));
        correctId = correct.id;
        break;
      }
      case "tradeToGeneric": {
        question = `What is the generic name for "${correct.trade}"?`;
        options = allOpts.map((d) => ({ id: d.id, text: d.generic }));
        correctId = correct.id;
        break;
      }
      case "classIdentify": {
        question = `${correct.generic} belongs to which drug class?`;
        const classes = [...new Set(drugs.map((d) => d.classLabel))];
        const wrongClasses = shuffle(
          classes.filter((cl) => cl !== correct.classLabel)
        ).slice(0, 3);
        const allLabels = shuffle([correct.classLabel, ...wrongClasses]);
        options = allLabels.map((cl, i) => ({
          id: cl === correct.classLabel ? "correct" : `wrong-${i}`,
          text: cl,
        }));
        correctId = "correct";
        break;
      }
      default:
        return null;
    }

    return { question, options, correctId, type };
  }, [drugs, showCascade]);

  const [q, setQ] = useState<any>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, correct: 0 });

  useEffect(() => {
    setQ(generateQuestion());
    setSelected(null);
    setStats({ total: 0, correct: 0 });
  }, [generateQuestion]);

  const handleSelect = (id: string) => {
    if (selected) return;
    setSelected(id);
    setStats((s) => ({
      total: s.total + 1,
      correct: s.correct + (id === q.correctId ? 1 : 0),
    }));
  };

  const nextQ = () => {
    setQ(generateQuestion());
    setSelected(null);
  };

  if (!q)
    return (
      <p style={{ color: "#64748b", textAlign: "center" }}>
        Need at least 4 drugs selected.
      </p>
    );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
          fontSize: 12,
          color: "#94a3b8",
        }}
      >
        <span>Question {stats.total || 1}</span>
        <span>
          {stats.correct}/{stats.total} correct (
          {stats.total > 0
            ? Math.round((stats.correct / stats.total) * 100)
            : 0}
          %)
        </span>
      </div>

      <div
        style={{
          padding: 20,
          borderRadius: 14,
          background: "#1e293b",
          border: "1px solid #334155",
          marginBottom: 16,
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 15,
            color: "#e2e8f0",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}
        >
          {q.question}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {q.options.map((opt: any, i: number) => {
          const isCorrect = opt.id === q.correctId;
          const isSelected = selected === opt.id;
          const showResult = selected !== null;

          let bg = "#1e293b";
          let borderColor = "#334155";
          let textColor = "#e2e8f0";

          if (showResult) {
            if (isCorrect) {
              bg = "rgba(34,197,94,0.12)";
              borderColor = "#16a34a";
              textColor = "#4ade80";
            } else if (isSelected && !isCorrect) {
              bg = "rgba(239,68,68,0.12)";
              borderColor = "#ef4444";
              textColor = "#fca5a5";
            } else {
              bg = "#1e293b";
              borderColor = "#1e293b";
              textColor = "#475569";
            }
          }

          return (
            <button
              key={i}
              onClick={() => handleSelect(opt.id)}
              style={{
                padding: "12px 16px",
                borderRadius: 10,
                textAlign: "left",
                background: bg,
                border: `1.5px solid ${borderColor}`,
                color: textColor,
                cursor: showResult ? "default" : "pointer",
                transition: "all 0.2s",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  marginRight: 8,
                  color: showResult ? textColor : "#64748b",
                }}
              >
                {String.fromCharCode(65 + i)}.
              </span>
              {opt.text}
              {showResult && isCorrect && " ✓"}
              {showResult && isSelected && !isCorrect && " ✕"}
            </button>
          );
        })}
      </div>

      {selected && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button
            onClick={nextQ}
            style={{
              padding: "10px 32px",
              borderRadius: 10,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Next Question →
          </button>
        </div>
      )}
    </div>
  );
}
